import { personRef, linkedPerson, resolvedSignatory } from "../../../utils/linkedScreeningPerson";
import ScreeningPendingSubject from "./ScreeningPendingSubject";
import ScreeningNameNotice from "./ScreeningNameNotice";
import { needsFullName } from "../../../utils/screeningName";
import { pepWebCandidates, pepWebClassification, pepWebIdentityLine, previousPepFindings, previousPepResearch, previousPepResearchLabel, applyWebResearchStatus, hasIncompletePepResearch, INCOMPLETE_PEP_RESEARCH_NOTICE } from "../../../utils/pepWebCandidates";
import { assessContext, overallIdentity, contextFingerprint, calendarBirthDate, VERSION as CONTEXT_VERSION } from "../../../utils/identityContext";
import IdentityContext, { ContextLimitation, MissingContextNote } from "./IdentityContext";
import GoogleSearchAttribution from "./GoogleSearchAttribution";
import { pepNameStrength, pepNameScore, pepNameReason } from "../../../utils/pepNameMatch";
import { orderScreeningHits } from "../../../utils/screeningHitOrder";
import { nameFields } from "../../../utils/individualName";
import { isSubjectReviewComplete, noMatchSubjectKeys, hasSubjectScreening, unchangedScreeningSubjects, retainSubjectScreening } from "../../../utils/exposureReview";
/* KycSectionPepWithScreening.jsx */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { auth } from "../../../firebase";

import Lottie from "lottie-react";
import pepAnim from "../../../images/pep-lottie.json";
import overallAlertAnim from "../../../images/alert-lottie.json";
import overallClearedAnim from "../../../images/cleared-lottie.json";
import overallPendingAnim from "../../../images/pending-lottie.json";
import handFingerUpIcon from "../../../images/hand-fingerup.svg";
import doubleTapFullIcon from "../../../images/double-tap-full.svg";
import userIcon from "../../../images/user.svg";
import userGreenIcon from "../../../images/user-d-green.svg";
import userRedIcon from "../../../images/user-d-red4.svg";
import userYellowIcon from "../../../images/user-d-yellow2.svg";
import { trackScreeningUsage } from "../../../utils/screeningUsage";
import { MYKYC_API_BASE } from "../../../config/api";

import "../css/KycSectionPepSanctionWithScreening.css";

/**
 * Staff-only PEP section for KycFormPage.
 *
 * Screens:
 *  - Always: Client name
 *  - If client is an Entity: also screens Ownership names:
 *      1) Direct shareholders
 *      2) UBOs
 *      3) Authorised signatories (from ownership.controllers)
 *
 * UI:
 *  - Each match line shows the ROLE(S) of the screened subject (Client / Direct shareholder / UBO / Authorised signatory)
 *  - Also shows the screened subject name that triggered the match.
 *
 * Saves to:
 *   pep: {
 *     clientDeclared: boolean,
 *     clientDetails: string,
 *     staffDecision: "no_exposure" | "flagged" | "flagged_remediated" | "flagged_confirmed" | null,
 *     staffAssessment: string,
 *   }
 *
 *   internalUse: {
 *     pepScreening: {
 *       searchedName: string,             // legacy (kept)
 *       searchedSubjects: Array<{         // ✅ new
 *          key: string,
 *          name: string,
 *          roles: string[],
 *          meta?: object
 *       }>,
 *       subjectSignature: string,         // ✅ used to detect changes
 *       result: {
 *         subjects: Array<{
 *            key: string,
 *            name: string,
 *            roles: string[],
 *            meta?: object,
 *            response: <API payload>      // { results: [...] }
 *         }>,
 *         errors: Array<{ key, name, message }>
 *       },
 *       personReviews: { [subjectKey]: { decision, decidedAt, decidedByUid } },
 *       relevantKeys: string[],           // reference markers only
 *       runAt: string | null,
 *     }
 *   }
 */

function safeStr(v) {
  return String(v == null ? "" : v);
}
function clean(v) {
  return safeStr(v)
    .replace(/\uFEFF/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^"(.*)"$/, "$1");
}
function autoResizeTextarea(el) {
  if (!el || typeof window === "undefined") return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight + verticalExtras;
  const maxHeight = lineHeight * 25 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}
function normKey(s) {
  return clean(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
}
function asList(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    return v
      .split(/;|\|/g)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
}
function takeFirst(arr) {
  return Array.isArray(arr) && arr.length ? clean(arr[0]) : "";
}
function joinAll(v, sep = " · ") {
  return asList(v).map(clean).filter(Boolean).join(sep);
}
function clampText(s, max = 120) {
  const t = clean(s);
  if (!t) return "";
  return t.length > max ? t.slice(0, max) + "…" : t;
}

function toTitleCaseWords(value) {
  return clean(value)
    .toLowerCase()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function pluralize(n, singular, plural) {
  return n === 1 ? singular : plural;
}

function screenedRolesText(subjects) {
  const arr = Array.isArray(subjects) ? subjects : [];
  const counts = {
    Client: 0,
    "Direct shareholder": 0,
    UBO: 0,
    "Authorised signatory": 0,
  };

  for (const s of arr) {
    const roles = Array.isArray(s?.roles) ? s.roles : [];
    const uniq = Array.from(
      new Set(roles.map((r) => clean(r)).filter(Boolean))
    );
    const lower = uniq.map((r) => r.toLowerCase());
    if (lower.some((r) => r.includes("client"))) counts.Client += 1;
    else if (lower.some((r) => r.includes("direct shareholder")))
      counts["Direct shareholder"] += 1;
    else if (
      lower.some(
        (r) =>
          r === "ubo" ||
          r.includes("ubo") ||
          r === "settlor" ||
          r === "trustee" ||
          r === "beneficiary"
      )
    )
      counts.UBO += 1;
    else if (lower.some((r) => r.includes("authorised signatory")))
      counts["Authorised signatory"] += 1;
  }

  const parts = [];
  if (counts.Client)
    parts.push(counts.Client === 1 ? "Client" : `${counts.Client} clients`);

  if (counts["Direct shareholder"])
    parts.push(
      `${counts["Direct shareholder"]} ${pluralize(
        counts["Direct shareholder"],
        "direct shareholder",
        "direct shareholders"
      )}`
    );

  if (counts.UBO)
    parts.push(`${counts.UBO} ${pluralize(counts.UBO, "UBO", "UBOs")}`);

  if (counts["Authorised signatory"])
    parts.push(
      `${counts["Authorised signatory"]} ${pluralize(
        counts["Authorised signatory"],
        "authorised signatory",
        "authorised signatories"
      )}`
    );

  return parts.join(" · ") || "Client";
}

function badge(text, scheme = "secondary") {
  const cls =
    scheme === "danger"
      ? "badge badgeStandard badgeKycRed"
      : scheme === "warning"
      ? "badge badgeStandard badgeKycYellow"
      : scheme === "success"
      ? "badge badgeStandard badgeKycGreen"
      : scheme === "grey"
      ? "badge badgeStandard badgeKycGrey"
      : scheme === "orange"
      ? "badge badgeStandard badgeKycOrange"
      : "badge badgeStandard badgeKycGrey";
  return <span className={cls}>{text}</span>;
}

/** ✅ squared role badge using your CSS classes */
function roleBadge(role) {
  const r = clean(role);
  if (!r) return null;
  return (
    <span
      className="squareBadge squareBadgeDarkBlue-outline"
      title="Screened role"
    >
      {r}
    </span>
  );
}

function strengthFromScore(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return "weak";
  if (s >= 0.9) return "strong";
  if (s >= 0.75) return "possible";
  return "weak";
}
function scorePill(strength) {
  if (strength === "strong") return badge("Strong", "danger");
  if (strength === "possible") return badge("Possible", "warning");
  return badge("Weak", "secondary");
}

function pepWebCategoryBadge(category) {
  const v = clean(category).toLowerCase();
  if (v === "pep") return badge("PEP", "grey");
  if (v === "pep_related" || v === "close_associate")
    return badge("Close to PEP", "grey");
  return badge("Not PEP", "grey");
}

function collectOpenSanctionsTopics(value, out = []) {
  if (!value) return out;
  if (typeof value === "string") {
    if (value) out.push(value);
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectOpenSanctionsTopics(item, out));
    return out;
  }
  if (typeof value === "object") {
    if (value.topics) collectOpenSanctionsTopics(value.topics, out);
    if (value.properties?.topics) collectOpenSanctionsTopics(value.properties.topics, out);
    if (value.entity?.topics) collectOpenSanctionsTopics(value.entity.topics, out);
    if (value.entity?.properties?.topics)
      collectOpenSanctionsTopics(value.entity.properties.topics, out);
  }
  return out;
}

function pepHitClassification(record) {
  const saved = record?.pepClassification || null;
  if (saved?.code) return saved;

  const topics = Array.from(
    new Set(
      collectOpenSanctionsTopics(record)
        .map((topic) => String(topic).trim())
        .filter(Boolean)
    )
  );
  if (topics.includes("role.pep")) {
    return { code: "direct_pep", label: "PEP", topics };
  }
  if (topics.includes("role.rca")) {
    return {
      code: "rca",
      label: "RCA / family member or close associate of a PEP",
      topics,
    };
  }
  return { code: "unknown", label: "PEP/RCA match", topics };
}

function pepHitClassificationBadge(classification) {
  const code = clean(classification?.code).toLowerCase();
  const sharedStyle = {
    background: "#AAACB2",
    color: "#fff",
  };
  if (code === "direct_pep") {
    return (
      <span className="badge badgeStandard" style={sharedStyle} title={classification?.label || "PEP"}>
        PEP
      </span>
    );
  }
  if (code === "rca") {
    return (
      <span
        className="badge badgeStandard"
        style={sharedStyle}
        title={classification?.label || "RCA / family member or close associate of a PEP"}
      >
        Close to PEP
      </span>
    );
  }
  return <span title={classification?.label || "PEP/RCA match"}>{badge("PEP/RCA", "grey")}</span>;
}

function isPepWebCategoryRelevant(category) {
  const v = clean(category).toLowerCase();
  return v === "pep" || v === "pep_related" || v === "close_associate";
}

function pepWebContextMatchBadge(outcome) {
  const v = clean(outcome).toLowerCase();
  if (v === "likely_match") return badge("Likely same person", "danger");
  if (v === "possible_match")
    return badge("Possibly same person", "warning");
  if (v === "likely_false_positive")
    return badge("Likely false positive", "success");
  return badge("Insufficient information", "grey");
}

function pepWebContextIcon(outcome) {
  const v = clean(outcome).toLowerCase();
  if (v === "likely_match") return userRedIcon;
  if (v === "possible_match") return userYellowIcon;
  if (v === "likely_false_positive") return userGreenIcon;
  return userIcon;
}

function defaultPepContextAssessment() {
  return {
    code: "no_context",
    label: "No context",
    checks: {
      nationality: "not_comparable",
      domicile: "not_comparable",
      age: "not_comparable",
    },
  };
}

function pepContextBadgeScheme(code) {
  if (code === "context_supports") return "danger";
  if (code === "context_mixed") return "warning";
  if (code === "context_conflicts") return "success";
  return "grey";
}

function pepContextBadgeLabel(context) {
  const code = clean(context?.code);
  if (code === "context_supports") return "Likely same person";
  if (code === "context_mixed") return "Possibly same person";
  if (code === "context_conflicts") return "Likely false positive";
  return "Context unclear";
}

function pepContextBadge(context) {
  const code = clean(context?.code);
  return badge(pepContextBadgeLabel(context), pepContextBadgeScheme(code));
}

function overallPepMatchAssessment(strength, context) { return overallIdentity(strength, context); }

function overallPepMatchBadge(strength, context) {
  const overall = overallPepMatchAssessment(strength, context);
  return badge(overall.label, overall.scheme);
}




function pepSignalFromAiCategory(category) {
  const v = clean(category).toLowerCase();
  if (v === "pep") return "pep";
  if (v === "pep_related" || v === "close_associate") return "close";
  return "";
}

function pepSignalFromMatchClassification(classification) {
  const code = clean(classification?.code).toLowerCase();
  if (code === "direct_pep") return "pep";
  if (code === "rca") return "close";
  return "";
}

function pepSignalBadge(label) {
  if (!label) return null;
  return (
    <span
      className="badge badgeStandard"
      style={{ background: "#AAACB2", color: "#fff" }}
    >
      {label}
    </span>
  );
}

function consolidatedPepSignalLabel(aiItem, matches) {
  const signals = new Set();
  const aiSignal = pepSignalFromAiCategory(aiItem?.result?.category);
  if (aiSignal) signals.add(aiSignal);
  (matches || []).forEach((match) => {
    const signal = pepSignalFromMatchClassification(match?.classification);
    if (signal) signals.add(signal);
  });

  if (signals.has("pep") && signals.has("close")) return "PEP / Close to PEP";
  if (signals.has("pep")) return "PEP";
  if (signals.has("close")) return "Close to PEP";
  if ((matches || []).length) return "PEP/RCA";
  return "";
}

function labelFromMatchType(matchObj) {
  const raw = matchObj ? JSON.stringify(matchObj) : "";
  const s = clean(raw).toLowerCase();
  if (!s) return "Potential match";

  if (s.includes("name_exact") || s.includes("exact")) return "Exact name";
  if (s.includes("alias")) return "Alias";
  if (s.includes("fuzzy")) return "Similar spelling";
  if (s.includes("token") || s.includes("overlap")) return "Similar words";
  return "Potential match";
}

// background color of the cards above the hits
function toneStyles({ hasResult, hasMatches }) {
  if (!hasResult) return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4C5465" };
  if (!hasMatches) return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4D5465" };
  return { bg: "#EDEAE9", border: "#ffd7bf", text: "#4D5465" };
}

// EDE8E5 EDEAE9

// function toneStyles({ hasResult, hasMatches }) {
//   if (!hasResult) return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4C5465" };
//   if (!hasMatches) return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4D5465" };
//   return { bg: "#F3F4F7", border: "#ffd7bf", text: "#4D5465" };
// }

function renderPepDetailsFromProperties(properties) {
  const p = properties || {};
  const pick = (k) => (Array.isArray(p?.[k]) ? p[k].filter(Boolean) : []);

  const lines = [];
  const names = pick("name");
  const firstName = pick("firstName");
  const lastName = pick("lastName");
  const position = pick("position");
  const birthDate = pick("birthDate");
  const alias = pick("alias");
  const notes = pick("notes");

  if (names.length) lines.push({ k: "Names", v: names.join(" · ") });
  if (firstName.length) lines.push({ k: "First name", v: firstName.join(" ") });
  if (lastName.length) lines.push({ k: "Last name", v: lastName.join(" ") });

  if (birthDate.length)
    lines.push({ k: "Birth date", v: birthDate.join(", ") });
  if (position.length) lines.push({ k: "Position", v: position.join(" · ") });
  if (notes.length) lines.push({ k: "Notes", v: notes.join(" · ") });

  if (alias.length)
    lines.push({
      k: "Alias",
      v:
        alias.slice(0, 8).join(" · ") +
        (alias.length > 8 ? ` (+${alias.length - 8} more)` : ""),
    });

  if (!lines.length) {
    return (
      <div className="small text-muted">No additional details available.</div>
    );
  }

  return (
    <div className="small">
      {lines.map((x, i) => (
        <div key={i} className="mb-1">
          <span className="text-muted">{x.k}:</span>{" "}
          <span style={{ color: "inherit" }}>{x.v}</span>
        </div>
      ))}
    </div>
  );
}

function summarizePepSnapshotFromRecord(record) {
  const props = record?.properties || record?.entity?.properties || {};
  const birthDate = takeFirst(props?.birthDate);
  const position = joinAll(props?.position, " · ");
  const notes = takeFirst(props?.notes);
  return { position, notes, birthDate };
}

function looksLikeEntityCustomer(form) {
  const t = clean(
    form?.customer?.type ||
      form?.customer?.customerType ||
      form?.customer?.clientType ||
      form?.customer?.personType ||
      form?.customer?.category ||
      ""
  ).toLowerCase();

  if (t.includes("entity") || t.includes("company") || t.includes("corporate"))
    return true;
  if (t.includes("individual") || t.includes("person")) return false;

  const sh = Array.isArray(form?.ownership?.shareholders)
    ? form.ownership.shareholders
    : [];
  const ub = Array.isArray(form?.ownership?.ubos) ? form.ownership.ubos : [];
  const ct = Array.isArray(form?.ownership?.signatories)
    ? form.ownership.signatories
    : [];

  const hasOwnershipNames =
    sh.some((x) => clean(x?.name)) ||
    ub.some((x) => clean(x?.name)) ||
    ct.some((x) => clean(x?.name));

  const hasEntityShareholder = sh.some(
    (x) => clean(x?.holderType).toLowerCase() === "entity"
  );

  return hasOwnershipNames || hasEntityShareholder;
}

function buildPepSubjects(form) {
  const subjects = [];
  const customer = form?.customer || {};
  const customerIsEntity = looksLikeEntityCustomer(form);

  const customerName = clean(form?.customer?.name);
  if (customerName) {
    subjects.push({
      key: `client:${normKey(customerName)}`,
      name: customerName,
      roles: ["Client"],
      meta: {
        subjectType: customerIsEntity ? "entity" : "individual",
        ...nameFields(customer),
        nationality: clean(customer?.nationalityOrIncorporationCountry || ""),
        domicileCountry: clean(customer?.country || ""),
        dateOfBirth: customer?.dobOrIncorporationDate || "",
        age: computeAgeFromDob(customer?.dobOrIncorporationDate),
      },
    });
  }

  const isEntity = customerIsEntity;
  if (!isEntity) return { isEntity: false, subjects };

  const ownership = form?.ownership || {};
  const shareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : [];
  const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
  const controllers = Array.isArray(ownership?.signatories)
    ? ownership.signatories
    : [];
  const legalArrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];

  const add = (name, role, meta) => {
    const nm = clean(name);
    if (!nm) return;
    subjects.push({
      key: `${role.toLowerCase().replace(/\s+/g, "_")}:${normKey(nm)}`,
      name: nm,
      roles: [role],
      meta: meta || {},
    });
  };

  shareholders.forEach((s, idx) => {
    const nm = clean(s?.name);
    if (!nm) return;
    const holderType = clean(s?.holderType).toLowerCase();
    add(nm, "Direct shareholder", {
      personRef: personRef("shareholders", s, idx),
      idx,
      subjectType:
        holderType === "entity" ||
        holderType === "company" ||
        holderType === "trust" ||
        holderType === "foundation"
          ? "entity"
          : "individual",
      holderType: clean(s?.holderType),
      beneficialOwnerType: clean(s?.beneficialOwnerType),
      ownershipPercent: clean(s?.ownershipPercent),
      ...nameFields(s),
      nationality: clean(s?.nationality || ""),
      domicileCountry: clean(s?.countryOfResidence || ""),
      dateOfBirth: s?.dateOfBirth || s?.dob || "",
    });
  });

  ubos.forEach((u, idx) => {
    const nm = clean(u?.name);
    if (!nm) return;
    add(nm, "UBO", {
      personRef: personRef("ubos", u, idx),
      idx,
      subjectType: "individual",
      basisOfControl: clean(u?.basisOfControl),
      relatedShareholderIdx: safeStr(u?.relatedShareholderIdx ?? ""),
      ...nameFields(u),
      nationality: clean(u?.nationality || ""),
      domicileCountry: clean(u?.countryOfResidence || ""),
      dateOfBirth: u?.dateOfBirth || u?.dob || "",
    });
  });

  controllers.forEach((stored, idx) => {
    const c = resolvedSignatory(ownership, stored);
    const linked = linkedPerson(ownership, stored);
    const nm = clean(c?.name);
    if (!nm) return;
    add(nm, "Authorised signatory", {
      personRef: linked?.ref || personRef("signatories", stored, idx),
      separatePerson: !linked,
      idx,
      subjectType: "individual",
      capacity: clean(c?.capacity),
      ...nameFields(c),
      nationality: clean(c?.nationality || ""),
      domicileCountry: clean(c?.countryOfResidence || ""),
      dateOfBirth: c?.dateOfBirth || c?.dob || "",
    });
  });

  const accountHolderTrust = legalArrangements.find((arr) => {
    const type = clean(arr?.arrangementType || arr?.type).toLowerCase();
    const source = clean(arr?.source).toLowerCase();
    return type === "trust" && source === "account_holder";
  });

  if (accountHolderTrust) {
    const parties = Array.isArray(accountHolderTrust?.parties)
      ? accountHolderTrust.parties
      : [];

    parties.forEach((p, idx) => {
      const nm = clean(p?.name);
      const role = clean(p?.role).toLowerCase();
      if (!nm || !role) return;
      if (/^class\b/i.test(nm) || /\bunnamed\b/i.test(nm)) return;

      if (role === "trustee") {
        const trusteeKind = clean(p?.trusteeKind).toLowerCase();
        if (trusteeKind === "company" || trusteeKind === "corporate") return;
        add(nm, "Trustee", {
          idx: `trustee_${idx}`,
          subjectType: "individual",
          ...nameFields(p),
          nationality: clean(p?.nationality || ""),
          domicileCountry: clean(p?.domicile || ""),
          dateOfBirth: p?.dateOfBirth || p?.dob || "",
        });
        return;
      }

      if (role === "settlor" || role === "beneficiary") {
        add(nm, role === "settlor" ? "Settlor" : "Beneficiary", {
          idx: `${role}_${idx}`,
          subjectType: "individual",
          ...nameFields(p),
          nationality: clean(p?.nationality || ""),
          domicileCountry: clean(p?.domicile || ""),
          dateOfBirth: p?.dateOfBirth || p?.dob || "",
        });
      }
    });
  }

  const map = new Map();
  for (const s of subjects) {
    const name = clean(s.name).toLowerCase();
    if (!name) continue;
    const linkedEntry = s.meta?.personRef && [...map.entries()].find(([, item]) => item.meta?.personRef === s.meta.personRef);
    const n = linkedEntry?.[0] || (s.meta?.separatePerson ? s.meta.personRef : name);
    // Unconfirmed signatories are independent people even when names coincide.
    if (!linkedEntry && s.meta?.separatePerson && [...map.values()].some(item => item.key === s.key)) s.key += `|${s.meta.personRef}`;
    const prev = map.get(n);
    if (!prev) {
      map.set(n, { ...s, roles: Array.from(new Set(s.roles)) });
    } else {
      const roles = new Set([...(prev.roles || []), ...(s.roles || [])]);
      map.set(n, {
        ...prev,
        roles: Array.from(roles),
        meta: linkedEntry ? prev.meta :
          prev.meta && s.meta
            ? { ...(prev.meta || {}), ...(s.meta || {}) }
            : prev.meta || s.meta || {},
      });
    }
  }

  const out = Array.from(map.values());
  out.sort((a, b) => {
    const aClient = a.roles?.includes("Client") ? 0 : 1;
    const bClient = b.roles?.includes("Client") ? 0 : 1;
    if (aClient !== bClient) return aClient - bClient;
    return a.name.localeCompare(b.name);
  });

  return { isEntity: true, subjects: out };
}

function buildSubjectsSignature(subjects) {
  return subjects
    .map(
      (s) =>
        `${s.key}|${clean(s.name).toLowerCase()}|${JSON.stringify([nameFields(s.meta), s.meta?.dateOfBirth, s.meta?.nationality, s.meta?.domicileCountry])}|${(s.roles || [])
          .slice()
          .sort()
          .join(",")}`
    )
    .join("||");
}

async function fetchPepForName(name, { limit, threshold, givenNames, familyName }) {
  const res = await fetch(
    `${MYKYC_API_BASE}/myKyc/screening/opensanctions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name, givenNames, familyName,
        limit: limit ?? 12,
        threshold: threshold ?? 0.7,
      }),
    }
  );

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) {
    const msg = json?.error || json?.message || "Screening failed";
    const err = new Error(msg);
    err._status = res.status;
    err._json = json;
    throw err;
  }
  if (!Array.isArray(json?.results)) throw new Error("PEP screening is currently unavailable");
  return json;
}

async function asyncPool(limit, items, worker) {
  const ret = [];
  const executing = [];
  for (const item of items) {
    const p = Promise.resolve().then(() => worker(item));
    ret.push(p);
    if (limit <= items.length) {
      const e = p.then(() => executing.splice(executing.indexOf(e), 1));
      executing.push(e);
      if (executing.length >= limit) await Promise.race(executing);
    }
  }
  return Promise.all(ret);
}

function computeAgeFromDob(value) {
  if (!value) return null;
  const d =
    value instanceof Date
      ? value
      : typeof value?.toDate === "function"
      ? value.toDate()
      : new Date(value);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

function buildScreeningUsageContext(form, screeningType) {
  return {
    orgId: clean(form?.orgId || form?.customer?.orgId || ""),
    clientId: clean(form?.clientId || form?.id || form?.customer?.clientId || ""),
    screeningType,
    source: "kyc",
  };
}

async function fetchPepWebAssessment({ name, givenNames, familyName, nationality, domicileCountry, dateOfBirth, age, usageContext, previousFindings }) {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("Sign in to run screening");
  const payload = {
    orgId: usageContext?.orgId || "",
    name, givenNames, familyName,
    nationality,
    domicileCountry,
    dateOfBirth: calendarBirthDate(dateOfBirth),
    age,
    screeningUsage: usageContext || {},
    previousFindings: previousFindings || [],
  };

  const res = await fetch(
    `${MYKYC_API_BASE}/myKyc/screening/pep_web_assessment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );

  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) {
    const msg = json?.error || json?.message || "PEP web assessment failed";
    const err = new Error(msg);
    err._status = res.status;
    err._json = json;
    throw err;
  }
  return json;
}

async function fetchPepContextAssessment({ subject, match }) {
  return assessContext(subject?.meta || {}, match, "pep");
}

/* ---------------- Framer Motion (minimal ROI) ---------------- */
// Temporarily hide the summary card while keeping screening evidence available.
const SHOW_AI_ASSESSMENT_CARD = false;
const SHOW_NO_EXPOSURE_CARD = false;

const T = {
  fast: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
  med: { duration: 0.26, ease: [0.22, 1, 0.36, 1] },
};

const cardIn = {
  initial: { opacity: 0, y: 10, scale: 0.995 },
  animate: { opacity: 1, y: 0, scale: 1, transition: T.med },
  exit: { opacity: 0, y: 8, scale: 0.995, transition: T.fast },
};

const detailsIn = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: T.med },
  exit: { opacity: 0, height: 0, transition: T.fast },
};

const fullDetailsExit = {
  overflow: "hidden",
  opacity: 0,
  height: 0,
  marginTop: 0,
  transition: {
    opacity: { duration: 0.12, ease: "easeOut" },
    height: { duration: 0.24, delay: 0.06, ease: [0.22, 1, 0.36, 1] },
    marginTop: { duration: 0.24, delay: 0.06, ease: [0.22, 1, 0.36, 1] },
  },
};

// Animate the clipped space rather than scaling the evidence or its text.
// Start closing after the fade begins, with a short overlap between the two.
const evidenceCollapse = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: "auto",
    transition: {
      height: { duration: 0.2, ease: "easeOut" },
      opacity: { duration: 0.14, delay: 0.06, ease: "easeOut" },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      opacity: { duration: 0.14, ease: "easeOut" },
      height: { duration: 0.2, delay: 0.08, ease: "easeInOut" },
    },
  },
};

const KycSectionPepWithScreening = forwardRef(function KycSectionPepWithScreening({
  sectionNo = 0,
  form,
  onSetAt,
  setAt,
  changedClass,
  readOnly = false,
  clientLocked = true,
  initiallyExpanded = false,
  onScreeningStateChange,
  hideScreeningSummary = false,
}, ref) {
  const writeAt = typeof onSetAt === "function" ? onSetAt : setAt;

  const pep = form?.pep || {};
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const customerName = useMemo(
    () => clean(form?.customer?.name || ""),
    [form?.customer?.name]
  );
  // Saving comments/reviews recreates form objects but must not restart research.
  const computedPepSubjects = buildPepSubjects(form);
  const pepSubjectsSnapshot = JSON.stringify(computedPepSubjects);
  const stablePepSubjectsRef = useRef(null);
  if (stablePepSubjectsRef.current?.snapshot !== pepSubjectsSnapshot) {
    // Retain Date/Firestore Timestamp objects for calendar-date handling.
    stablePepSubjectsRef.current = { snapshot: pepSubjectsSnapshot, value: computedPepSubjects };
  }
  const { isEntity, subjects: allPepSubjects } = stablePepSubjectsRef.current.value;
  const blockedSubjects = useMemo(() => allPepSubjects.filter(needsFullName), [allPepSubjects]);
  const pepSubjects = useMemo(() => allPepSubjects.filter(subject => !needsFullName(subject)), [allPepSubjects]);
  const pepWebAssessmentSubjects = useMemo(
    () =>
      (pepSubjects || []).filter(
        (s) => clean(s?.meta?.subjectType).toLowerCase() !== "entity"
      ),
    [pepSubjects]
  );
  const pepWebAssessmentSignature = useMemo(
    () =>
      pepWebAssessmentSubjects
        .map((s) =>
          [
            clean(s?.key),
            clean(s?.name).toLowerCase(),
            JSON.stringify(nameFields(s?.meta)),
          ].join("|")
        )
        .join("||"),
    [pepWebAssessmentSubjects]
  );
  const pepContextAssessmentSignature = useMemo(
    () =>
      (pepSubjects || [])
        .map((s) =>
          [
            clean(s?.key),
            clean(s?.name).toLowerCase(),
            clean(s?.meta?.nationality).toUpperCase(),
            CONTEXT_VERSION,
            clean(s?.meta?.domicileCountry || ""),
            clean(s?.meta?.dateOfBirth || ""),
            s?.meta?.age ?? "",
          ].join("|")
        )
        .join("||"),
    [pepSubjects]
  );

  const subjectSignature = useMemo(
    () => buildSubjectsSignature(allPepSubjects),
    [allPepSubjects]
  );

  // ---------------- PEP SCREENING LOCAL STATE ----------------
  const autoClearRunRef = useRef(null);
  const screeningRunTokenRef = useRef(0);
  const previousSubjectsRef = useRef(pepSubjects);
  const [runningSubjectKey, setRunningSubjectKey] = useState(null);
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [screeningErr, setScreeningErr] = useState("");
  const [screeningResult, setScreeningResult] = useState(null);

  const [openKey, setOpenKey] = useState(null);
  const [pepReviewScrolled, setPepReviewScrolled] = useState(false);

  const hasResult = !!screeningResult;

  const [pepWebAssessmentLoading, setPepWebAssessmentLoading] = useState(false);
  const [pepWebAssessmentErr, setPepWebAssessmentErr] = useState("");
  const [pepWebAssessment, setPepWebAssessment] = useState([]);
  const pepPreviousResearchRef = useRef(null);
  const pepWebRequestRef = useRef(null);
  const prevSigRef = useRef(subjectSignature);
  const clientCommentsRef = useRef(null);
  const complianceCommentsRef = useRef(null);
  const visiblePepWebAssessment = useMemo(
    () =>
      (pepWebAssessment || []).flatMap(pepWebCandidates),
    [pepWebAssessment]
  );

  function resetComplianceAssessment() {
    if (typeof writeAt !== "function") return;
    writeAt(["pep", "staffDecision"], null);
    writeAt(["pep", "staffAssessment"], "");
  }

  function persistScreeningState(partial) {
    if (typeof writeAt !== "function") return;
    const saved = form?.internalUse?.pepScreening || {};
    const next = {
      searchedName: customerName || "",
      searchedSubjects: pepSubjects || [],
      subjectSignature: subjectSignature || "",
      runAt: saved.runAt ?? null,
      result: saved.result ?? null,
      personReviews: personDecisions,
      relevantKeys: Array.from(relevantKeys),
      ...(partial || {}),
    };
    // A transiently-null component state must never wipe the saved result.
    if (next.result == null && saved.result != null) next.result = saved.result;
    writeAt(["internalUse", "pepScreening"], next);
  }

  function persistPepWebAssessment(partial) {
    if (typeof writeAt !== "function") return;
    writeAt(["internalUse", "pepWebAssessment"], partial || null);
  }

  // --- Toggle visibibility details section ---
  // Show/hide everything below the TOP SUMMARY CARD - true : visible, false: hidden
  const [showDetails, setShowDetails] = useState(!!initiallyExpanded);
  // Bis layout port: extensions row is clipped while its height animates, then
  // released so the middle arrow can overhang into the screening card below.
  const [extRevealed, setExtRevealed] = useState(!!initiallyExpanded);
  const [complianceAssessmentGlowActive, setComplianceAssessmentGlowActive] =
    useState(false);
  const [
    complianceAssessmentIntroBackgroundActive,
    setComplianceAssessmentIntroBackgroundActive,
  ] = useState(false);
  const [actionRequiredBadgeVisible, setActionRequiredBadgeVisible] =
    useState(true);
  const actionRequiredBadgeRevealTimerRef = useRef(null);
  const [collapseReviewSnapshot, setCollapseReviewSnapshot] = useState(null);
  const collapseScrollPartsRef = useRef(null);
  const collapseScrollFrameRef = useRef(null);

  function cancelDetailsCollapse() {
    window.cancelAnimationFrame(collapseScrollFrameRef.current);
    collapseScrollPartsRef.current = null;
    setCollapseReviewSnapshot(null);
  }

  function toggleDetails() {
    cancelDetailsCollapse();
    setShowDetails((v) => !v);
  }

  function finishCollapseScroll(part) {
    const pending = collapseScrollPartsRef.current;
    if (!pending) return;
    pending.delete(part);
    if (pending.size) return;
    collapseScrollPartsRef.current = null;
    setCollapseReviewSnapshot(null);
  }

  function scrollPepAnchorThenCollapse() {
    window.cancelAnimationFrame(collapseScrollFrameRef.current);
    // Freeze evidence visibility while the new outcome appears in the header.
    // Otherwise the individual hit exits compete with the page scroll.
    setCollapseReviewSnapshot({ personDecisions, decisionEditKeys });
    collapseScrollPartsRef.current = new Set([
      "extensions",
      ...(hasResult || screeningLoading ? ["results"] : []),
    ]);
    collapseScrollFrameRef.current = window.requestAnimationFrame(() => {
      const anchor = document.getElementById("kyc-sec-pep");
      if (!anchor || anchor.getBoundingClientRect().top >= 0) {
        setShowDetails(false);
        return;
      }
      anchor.scrollIntoView({ behavior: "smooth", block: "start" });
      const startedAt = performance.now();
      let lastTop = anchor.getBoundingClientRect().top;
      let stableFrames = 0;
      const waitForScroll = (now) => {
        const top = anchor.getBoundingClientRect().top;
        stableFrames = Math.abs(top - lastTop) < 0.5 ? stableFrames + 1 : 0;
        lastTop = top;
        // Follow actual scroll completion, including reduced-motion or clamped
        // scrolling, instead of closing midway through a fixed 500ms timeout.
        if (Math.abs(top) <= 1 || (now - startedAt > 120 && stableFrames >= 3) || now - startedAt > 2000) {
          setShowDetails(false);
          return;
        }
        collapseScrollFrameRef.current = window.requestAnimationFrame(waitForScroll);
      };
      collapseScrollFrameRef.current = window.requestAnimationFrame(waitForScroll);
    });
  }

  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(collapseScrollFrameRef.current);
      if (actionRequiredBadgeRevealTimerRef.current) {
        window.clearTimeout(actionRequiredBadgeRevealTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      autoResizeTextarea(complianceCommentsRef.current);
    });
  }, [pep?.staffAssessment]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      autoResizeTextarea(clientCommentsRef.current);
    });
  }, [pep?.clientDetails]);

  // ---------------- HYDRATE FROM SAVED KYC (schema v2) ----------------
  useEffect(() => {
    const saved = form?.internalUse?.pepScreening || null;
    setScreeningResult(saved?.result || null);
    setPersonDecisions(
      saved?.personReviews && typeof saved.personReviews === "object"
        ? saved.personReviews
        : {}
    );
    setRelevantKeys(
      new Set(Array.isArray(saved?.relevantKeys) ? saved.relevantKeys : [])
    );
  }, [form?.internalUse?.pepScreening]);

  useEffect(() => {
    const saved = form?.internalUse?.pepWebAssessment || null;
    if (!saved) {
      setPepWebAssessment([]);
      setPepWebAssessmentErr("");
      setPepWebAssessmentLoading(false);
      return;
    }

    setPepWebAssessment(Array.isArray(saved?.items) ? saved.items : []);
    if (saved?.items?.length || saved?.previousItems?.length) pepPreviousResearchRef.current = saved;
    setPepWebAssessmentErr(saved.refreshFailed ? "Public-web refresh is currently unavailable" : hasIncompletePepResearch(saved) ? INCOMPLETE_PEP_RESEARCH_NOTICE : "");
    setPepWebAssessmentLoading(false);
  }, [form?.internalUse?.pepWebAssessment]);

  useEffect(() => {
    if (readOnly || screeningLoading) return;
    if (!hasResult) return;
    if (!pepWebAssessmentSubjects.length) return;
    if (!pepWebAssessmentSignature) return;
    if (prevSigRef.current !== subjectSignature) return;

    const saved = form?.internalUse?.pepWebAssessment || null;
    if (saved?.refreshFailed && saved.signature === pepWebAssessmentSignature) return;
    const screenedSubjects = pepWebAssessmentSubjects.filter(subject => hasSubjectScreening(screeningResult, subject));
    const existingItems = (saved?.items || []).filter(item => screenedSubjects.some(subject => subject.key === item.subjectKey));
    const subjectsToResearch = screenedSubjects.filter(subject => !existingItems.some(item =>
      item.subjectKey === subject.key && String(item.result?.assessment || '').trim() &&
      clean(item.subjectName).toLowerCase() === clean(subject.name).toLowerCase()));
    if (!subjectsToResearch.length) return;

    let cancelled = false;
    const prior = saved?.previousItems?.length ? saved : pepPreviousResearchRef.current || saved;
    const priorItems = prior?.previousItems?.length ? prior.previousItems : prior?.items || [];

    async function run() {
      setPepWebAssessmentLoading(true);
      setPepWebAssessmentErr("");
      try {
        const requestKey = JSON.stringify([
          buildScreeningUsageContext(form, "pep"),
          screeningResult?.runAt,
          subjectsToResearch,
        ]);
        // Effect cleanup cancels its subscriber, not the paid request. A new
        // subscriber (including Strict Mode replay) awaits the same promise.
        if (pepWebRequestRef.current?.key !== requestKey) {
          pepWebRequestRef.current = { key: requestKey, promise: (async () => {
            const items = [];
            for (const subj of subjectsToResearch) {
              const json = await fetchPepWebAssessment({
                previousFindings: previousPepFindings(priorItems, subj),
                name: clean(subj?.name),
                ...nameFields(subj.meta),
                nationality: clean(subj?.meta?.nationality || ""),
                domicileCountry: clean(subj?.meta?.domicileCountry || ""),
                dateOfBirth: subj?.meta?.dateOfBirth || "",
                age:
                  subj?.meta?.age === null || subj?.meta?.age === undefined
                    ? null
                    : Number(subj.meta.age),
                usageContext: {
                  ...buildScreeningUsageContext(form, "pep"),
                  subjectName: clean(subj?.name),
                  subjectKey: clean(subj?.key),
                },
              });

              items.push({
                subjectKey: clean(subj?.key),
                subjectName: clean(subj?.name),
                roles: Array.isArray(subj?.roles) ? subj.roles : [],
                query: json?.query || {
                  name: clean(subj?.name),
                  ...nameFields(subj.meta),
                  nationality: clean(subj?.meta?.nationality || ""),
                  domicileCountry: clean(subj?.meta?.domicileCountry || ""),
                  age:
                    subj?.meta?.age === null || subj?.meta?.age === undefined
                      ? null
                      : Number(subj.meta.age),
                },
                result: json?.result || null,
                researchedAt: json?.result?.researchedAt || Date.now(),
              });
            }
            return items;
          })() };
        }
        const freshItems = await pepWebRequestRef.current.promise;
        const requestedKeys = new Set(subjectsToResearch.map(subject => subject.key));
        const items = [...existingItems.filter(item => !requestedKeys.has(item.subjectKey)), ...freshItems];

        if (cancelled) return;

        const payload = {
          signature: pepWebAssessmentSignature,
          items,
          researchIncomplete: hasIncompletePepResearch({items}),
          updatedAt: Date.now(),
        };

        setPepWebAssessmentErr(payload.researchIncomplete ? INCOMPLETE_PEP_RESEARCH_NOTICE : "");
        setPepWebAssessment(items);
        persistPepWebAssessment(payload);
      } catch (e) {
        if (cancelled) return;
        const requestedKeys = new Set(subjectsToResearch.map(subject => subject.key));
        const items = [...existingItems.filter(item => !requestedKeys.has(item.subjectKey)), ...previousPepResearch(prior, subjectsToResearch)];
        setPepWebAssessmentErr("Public-web research is currently unavailable");
        setPepWebAssessment(items);
        persistPepWebAssessment({signature:pepWebAssessmentSignature,items,refreshFailed:true,updatedAt:prior?.updatedAt || null});
      } finally {
        if (!cancelled) setPepWebAssessmentLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [
    readOnly,
    hasResult,
    screeningLoading,
    screeningResult,
    pepWebAssessmentSubjects,
    pepWebAssessmentSignature,
    form?.internalUse?.pepWebAssessment,
  ]);

  const pepContextRefreshRef = useRef("");
  useEffect(() => {
    if (readOnly || screeningLoading || prevSigRef.current !== subjectSignature) return;
    if (!screeningResult || !Array.isArray(screeningResult?.subjects)) return;
    if (!pepContextAssessmentSignature) return;
    if (pepContextRefreshRef.current === pepContextAssessmentSignature) return;

    let cancelled = false;

    async function refreshPepContextOnly() {
      const subjectByKey = new Map(
        (pepSubjects || []).map((subject) => [clean(subject?.key), subject])
      );

      const updatedSubjects = await asyncPool(
        3,
        screeningResult.subjects,
        async (subjectResult) => {
          const subject =
            subjectByKey.get(clean(subjectResult?.key)) || subjectResult || {};
          const matches = Array.isArray(subjectResult?.response?.results)
            ? subjectResult.response.results
            : [];
          const results = await asyncPool(4, matches, async (match) => ({
            ...match,
            contextAssessment: await fetchPepContextAssessment({
              subject,
              match,
            }),
          }));

          return {
            ...subjectResult,
            meta: subject?.meta || subjectResult?.meta || {},
            response: {
              ...(subjectResult?.response || {}),
              results,
            },
          };
        }
      );

      if (cancelled) return;

      const updatedResult = {
        ...screeningResult,
        subjects: updatedSubjects,
      };

      pepContextRefreshRef.current = pepContextAssessmentSignature;
      setScreeningResult(updatedResult);
      persistScreeningState({ result: updatedResult });
    }

    refreshPepContextOnly().catch(() => {
      pepContextRefreshRef.current = "";
    });

    return () => {
      cancelled = true;
    };
  }, [
    form?.internalUse?.pepScreening,
    pepContextAssessmentSignature,
    pepSubjects,
    readOnly,
    screeningLoading,
    screeningResult,
  ]);

  // ---- Person-grouped review (schema v2) ----
  // personDecisions mirrors internalUse.pepScreening.personReviews:
  // { [subjectKey]: { decision, decidedAt, decidedByUid } }. relevantKeys
  // mirrors .relevantKeys. Legacy selectedKeys/reviewStatus no longer exist
  // (fresh-data project decision, 23 Sep).
  const [personDecisions, setPersonDecisions] = useState({});
  // Groups temporarily reopened via the chip's "Change" affordance.
  const [decisionEditKeys, setDecisionEditKeys] = useState(() => new Set());
  const [relevantKeys, setRelevantKeys] = useState(() => new Set());

  function openDecisionEdit(groupKey) {
    if (readOnly) return;
    cancelDetailsCollapse();
    setDecisionEditKeys((prev) => {
      const next = new Set(prev);
      next.add(groupKey);
      return next;
    });
  }

  function setPersonDecision(groupKey, decision) {
    if (readOnly || (decision === "no_exposure" && subjectHasSelectedEvidence(groupKey))) return;
    cancelDetailsCollapse();
    // Either option confirms the review; the separate clear button resets it.
    const outcomeChanged = personDecisions[groupKey]?.decision !== decision;
    const next = { ...personDecisions };
    if (decision == null) delete next[groupKey];
    else next[groupKey] = {
      decision,
      contextFingerprint: contextFingerprint(pepSubjects.find(s => s.key === groupKey)?.meta),
      contextRuleVersion: CONTEXT_VERSION,
      decidedAt: new Date().toISOString(),
      decidedByUid: auth?.currentUser?.uid || "",
    };
    setPersonDecisions(next);
    setDecisionEditKeys((prev) => {
      const nextKeys = new Set(prev);
      nextKeys.delete(groupKey);
      return nextKeys;
    });
    persistScreeningState({ personReviews: next });

    updateReviewCompletion(next, relevantKeys, pep?.supportingInformation, outcomeChanged);
  }

  function updateReviewCompletion(next, selectedKeys, notes, outcomeChanged = false) {
    const allSubjectsReviewed = !blockedSubjects.length && pepSubjects.length > 0 && pepSubjects.every(
      (subject) => isSubjectReviewed(subject.key, next, selectedKeys, notes)
    );
    if (!outcomeChanged && allSubjectsReviewed === isReviewCompleted) return;
    if (actionRequiredBadgeRevealTimerRef.current) {
      window.clearTimeout(actionRequiredBadgeRevealTimerRef.current);
      actionRequiredBadgeRevealTimerRef.current = null;
    }
    if (!allSubjectsReviewed && typeof writeAt === "function") {
      writeAt(["pep", "staffDecision"], null);
    }
    const exposureIdentified = pepSubjects.some(
      (subject) => next[subject.key]?.decision === "exposure"
    );
    if (allSubjectsReviewed && !exposureIdentified) {
      scrollPepAnchorThenCollapse();
    }
    if (allSubjectsReviewed && exposureIdentified && !phase2) {
      setActionRequiredBadgeVisible(false);
      setComplianceAssessmentIntroBackgroundActive(true);
      setComplianceAssessmentGlowActive(true);
      actionRequiredBadgeRevealTimerRef.current = window.setTimeout(() => {
        setActionRequiredBadgeVisible(true);
        actionRequiredBadgeRevealTimerRef.current = null;
      }, 720);
    } else {
      setActionRequiredBadgeVisible(true);
      setComplianceAssessmentIntroBackgroundActive(false);
      setComplianceAssessmentGlowActive(false);
    }
  }

  function toggleRelevant(evidenceKey) {
    if (readOnly) return;
    const next = new Set(relevantKeys);
    if (next.has(evidenceKey)) next.delete(evidenceKey);
    else next.add(evidenceKey);
    setRelevantKeys(next);
    const group = groupedPepReviewItems.find(item =>
      item.aiItems.some(candidate => candidate.evidenceKey === evidenceKey) ||
      item.matches.some(match => match._key === evidenceKey)
    );
    const clearOutcome = next.has(evidenceKey) && personDecisions[group?.key]?.decision === "no_exposure";
    const nextDecisions = { ...personDecisions };
    if (clearOutcome) {
      cancelDetailsCollapse();
      delete nextDecisions[group.key];
      setPersonDecisions(nextDecisions);
    }
    persistScreeningState({ relevantKeys: Array.from(next), personReviews: nextDecisions });
    updateReviewCompletion(nextDecisions, next, pep?.supportingInformation, clearOutcome);
  }

  function setSupportingInformation(groupKey, value) {
    if (readOnly) return;
    const notes = { ...pep?.supportingInformation, [groupKey]: value };
    writeAt?.(["pep", "supportingInformation", groupKey], value);
    updateReviewCompletion(personDecisions, relevantKeys, notes);
  }


  // ---------------- RESET SCREENING + COMPLIANCE IF SUBJECTS CHANGE ----------------
  useEffect(() => {
    const prev = prevSigRef.current;
    if (prev !== subjectSignature) {
      screeningRunTokenRef.current += 1;
      autoClearRunRef.current = null;
      setScreeningLoading(false);
      setRunningSubjectKey(null);
      const retainedSubjects = unchangedScreeningSubjects(previousSubjectsRef.current, pepSubjects);
      previousSubjectsRef.current = pepSubjects;
      const retainedKeys = new Set(retainedSubjects.map(subject => subject.key));
      const retainedResult = retainSubjectScreening(screeningResult, retainedSubjects);
      const retainedReviews = Object.fromEntries(Object.entries(personDecisions).filter(([key]) => retainedKeys.has(key)));
      const evidenceKeys = new Set(groupedPepReviewItems.filter(group => retainedKeys.has(group.key)).flatMap(group => [
        ...group.matches.map(match => match._key), ...(group.aiItems || []).map(item => item.evidenceKey),
      ]));
      const retainedSelections = [...relevantKeys].filter(key => evidenceKeys.has(key));
      pepWebRequestRef.current = null;
      pepPreviousResearchRef.current = null;
      setScreeningErr("");
      setScreeningResult(retainedResult);
      const retainedItems = pepWebAssessment.filter(item => retainedKeys.has(item.subjectKey));
      const retainedWeb = { ...form?.internalUse?.pepWebAssessment, items: retainedItems, previousItems: [], signature: pepWebAssessmentSignature };
      setPepWebAssessment(retainedItems);
      pepContextRefreshRef.current = pepContextAssessmentSignature;
      setPepWebAssessmentErr("");
      setPepWebAssessmentLoading(false);
      setOpenKey(null);
      setPersonDecisions(retainedReviews);
      setRelevantKeys(new Set(retainedSelections));

      if (typeof writeAt === "function") {
        writeAt(["internalUse", "pepScreening"], retainedResult ? {
          ...form?.internalUse?.pepScreening, result: retainedResult, searchedSubjects: retainedSubjects,
          subjectSignature, personReviews: retainedReviews, relevantKeys: retainedSelections,
          reviewStatus: null, selectionSaved: false,
        } : null);
        writeAt(["internalUse", "pepWebAssessment"], retainedWeb);
      }

      resetComplianceAssessment();
      prevSigRef.current = subjectSignature;
    }
  }, [subjectSignature, writeAt]);

  // ---------------- RUN SCREENING ----------------
  async function runPepScreening(targetSubject = null) {
    if (readOnly || screeningLoading || (targetSubject?.key && pepWebAssessmentLoading)) return;
    const target = targetSubject?.key ? pepSubjects.find(subject => subject.key === targetSubject.key) : null;
    if (targetSubject?.key && (!target || hasSubjectScreening(screeningResult, target))) return;
    const subjectsToRun = target ? [target] : pepSubjects;
    const keptReviews = target ? personDecisions : {};
    const keptSelections = target ? Array.from(relevantKeys) : [];
    setRunningSubjectKey(target?.key || null);
    const runToken = ++screeningRunTokenRef.current;
    pepWebRequestRef.current = null;
    autoClearRunRef.current = null;
    const prior = form?.internalUse?.pepWebAssessment || pepPreviousResearchRef.current;
    const previousItems = previousPepResearch(prior, pepWebAssessmentSubjects);
    const pendingResearch = {items: target ? pepWebAssessment : [], previousItems, updatedAt:prior?.updatedAt || null};
    pepPreviousResearchRef.current = pendingResearch;
    cancelDetailsCollapse();
    setShowDetails(true);
    setScreeningErr("");
    if (!target) setScreeningResult(null);
    if (!target) setPepWebAssessment([]);
    setPepWebAssessmentErr("");
    setPepWebAssessmentLoading(false);
    setOpenKey(null);
    // Rerun rule (owner default, 23 Sep): new evidence starts a fresh review —
    // person decisions and evidence selections are cleared, never carried over.
    if (!target) setPersonDecisions({});
    if (!target) setRelevantKeys(new Set());

    if (typeof writeAt === "function") {
      if (!target) writeAt(["internalUse", "pepScreening"], null);
      writeAt(["internalUse", "pepWebAssessment"], pendingResearch);
    }

    resetComplianceAssessment();

    if (!pepSubjects || pepSubjects.length === 0) {
      setScreeningErr(blockedSubjects.length ? "" : "Missing customer name.");
      return;
    }

    setScreeningLoading(true);

    try {
      const errors = [];
      const subjectResponses = [];
      for (const subj of subjectsToRun) {
        if (runToken !== screeningRunTokenRef.current) return;
        try {
          const response = await fetchPepForName(subj.name, {
            ...nameFields(subj.meta),
            limit: 12,
            threshold: 0.7,
          });
          if (runToken !== screeningRunTokenRef.current) return;
          const matches = Array.isArray(response?.results)
            ? response.results
            : [];
          const resultsWithContext = await asyncPool(4, matches, async (match) => ({
            ...match,
            contextAssessment: await fetchPepContextAssessment({
              subject: subj,
              match,
            }),
          }));
          subjectResponses.push({
            key: subj.key,
            name: subj.name,
            roles: subj.roles || [],
            meta: subj.meta || {},
            response: {
              ...response,
              results: resultsWithContext,
            },
          });
        } catch (e) {
          errors.push({
            key: subj.key,
            name: subj.name,
            message: e?.message || "Screening failed",
          });
        }
      }

      await trackScreeningUsage({
        form,
        screeningType: "pep",
        namesCount: subjectsToRun.length,
        subjects: subjectsToRun,
      });

      const combined = { subjects: [...(target ? screeningResult?.subjects || [] : []), ...subjectResponses],
        errors: [...(target ? screeningResult?.errors || [] : []), ...errors] };

      if (runToken !== screeningRunTokenRef.current) return;
      const runAt = new Date().toISOString();
      autoClearRunRef.current = runAt;
      setScreeningResult(combined);

      // Public-web research must also finish before an empty search can be cleared.
      persistScreeningState({
        result: combined,
        personReviews: keptReviews,
        relevantKeys: keptSelections,
        runAt,
      });
    } catch (e) {
      if (runToken === screeningRunTokenRef.current) setScreeningErr(e?.message || "Screening failed.");
    } finally {
      if (runToken === screeningRunTokenRef.current) { setScreeningLoading(false); setRunningSubjectKey(null); }
    }
  }

  useEffect(() => {
    onScreeningStateChange?.(screeningLoading || pepWebAssessmentLoading);
  }, [onScreeningStateChange, screeningLoading, pepWebAssessmentLoading]);

  useImperativeHandle(
    ref,
    () => ({
      runScreening: runPepScreening,
      hasRun: hasResult,
      isLoading: screeningLoading || pepWebAssessmentLoading,
      canRun: !readOnly && !screeningLoading && !!pepSubjects?.length,
    }),
    [hasResult, screeningLoading, pepWebAssessmentLoading, readOnly, pepSubjects]
  );

  function clearScreening() {
    autoClearRunRef.current = null;
    pepPreviousResearchRef.current = null;
    setScreeningErr("");
    setScreeningResult(null);
    setPepWebAssessment([]);
    setPepWebAssessmentErr("");
    setPepWebAssessmentLoading(false);
    setOpenKey(null);
    setPersonDecisions({});
    setRelevantKeys(new Set());

    if (typeof writeAt === "function") {
      writeAt(["internalUse", "pepScreening"], null);
      writeAt(["internalUse", "pepWebAssessment"], null);
    }

    resetComplianceAssessment();
  }

  // ---------------- MATCH NORMALIZATION (PER SUBJECT) ----------------
  const uniqueMatches = useMemo(() => {
    const subjects = Array.isArray(screeningResult?.subjects)
      ? screeningResult.subjects
      : [];
    if (!subjects.length) return [];

    const out = [];

    for (const subj of subjects) {
      const subjKey = clean(subj?.key);
      const subjName = clean(subj?.name);
      const subjRoles = Array.isArray(subj?.roles) ? subj.roles : [];
      const subjMeta = subj?.meta || {};

      const results = Array.isArray(subj?.response?.results)
        ? subj.response.results
        : [];
      for (const m of results) {
        const id = clean(m?.id) || "";
        const pepName = clean(m?.caption) || clean(m?.name) || "Unknown";
        const key = `${subjKey || "subject"}|${
          id ? `OS:${id}` : `OS:${pepName.toLowerCase()}`
        }`;

        const strength = pepNameStrength(m);
        const classification = pepHitClassification(m);

        out.push({
          _key: key,
          id,
          name: pepName,
          score: pepNameScore(m),
          strength,
          classification,
          matchType: m?.match || null,
          record: m,
          contextAssessment: m?.contextAssessment || defaultPepContextAssessment(),
          subject: {
            key: subjKey,
            name: subjName,
            roles: subjRoles,
            meta: subjMeta,
          },
        });
      }
    }

    const rank = (s) => (s === "strong" ? 0 : s === "possible" ? 1 : 2);

    out.sort(
      (a, b) =>
        rank(strengthFromScore(a.record?.score)) - rank(strengthFromScore(b.record?.score)) || a.name.localeCompare(b.name)
    );

    return out;
  }, [screeningResult]);

  const styles = useMemo(
    () =>
      toneStyles({
        hasResult: !!screeningResult,
        hasMatches: uniqueMatches.length > 0,
      }),
    [screeningResult, uniqueMatches.length]
  );

  // Keep every hit in its person group; filter the displayed evidence after
  // a person-level decision so editing the outcome can reveal all hits again.
  const visibleMatches = useMemo(
    () => (screeningResult ? uniqueMatches : []),
    [screeningResult, uniqueMatches]
  );

  const pepReviewHeaderSticky = true;
  const pepReviewHeaderGlass = pepReviewHeaderSticky && pepReviewScrolled;

  const groupedPepReviewItems = useMemo(() => {
    const map = new Map();
    const subjectOrder = new Map();
    (allPepSubjects || []).forEach((subject, index) => {
      const key = clean(subject?.key) || `subject_${index}`;
      subjectOrder.set(key, index);
    });

    function ensureGroup({ key, name, roles, meta }) {
      const groupKey = clean(key) || `subject:${normKey(name || "subject")}`;
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          name: clean(name) || "Subject",
          roles: Array.isArray(roles) ? roles : [],
          meta: meta || {},
          matches: [],
          aiItem: null,
          aiItems: [],
          order: subjectOrder.has(groupKey)
            ? subjectOrder.get(groupKey)
            : Number.MAX_SAFE_INTEGER,
        });
      }
      const group = map.get(groupKey);
      if (!group.name && name) group.name = clean(name);
      if (!group.roles.length && Array.isArray(roles)) group.roles = roles;
      if (!Object.keys(group.meta || {}).length && meta) group.meta = meta;
      return group;
    }

    // A screened person still needs an outcome and a place for manual evidence
    // when neither the watchlist nor public-web research returns a hit.
    (allPepSubjects || []).forEach(ensureGroup);

    (visibleMatches || []).forEach((match) => {
      const subject = match?.subject || {};
      const group = ensureGroup({
        key: subject.key,
        name: subject.name,
        roles: subject.roles,
        meta: subject.meta,
      });
      group.matches.push(match);
    });

    (visiblePepWebAssessment || []).forEach((item, index) => {
      const group = ensureGroup({
        key: item?.subjectKey || `ai_${index}`,
        name: item?.subjectName,
        roles: item?.roles,
        meta: {},
      });
      group.aiItem ||= item;
      group.aiItems.push(item);
    });

    if (
      (pepWebAssessmentLoading || pepWebAssessmentErr) &&
      !(visiblePepWebAssessment || []).length
    ) {
      (pepWebAssessmentSubjects || []).forEach((subject, index) => {
        ensureGroup({
          key: subject?.key || `assessment_${index}`,
          name: subject?.name,
          roles: subject?.roles,
          meta: subject?.meta,
        });
      });
    }

    const groups = Array.from(map.values());
    // Use the same current-context assessment as the visible identity badges.
    // Watchlist and public-web results arrive as complete candidate batches, so
    // each list can be ordered immediately without waiting for other subjects.
    for (const group of groups) {
      group.matches = orderScreeningHits(group.matches, match => ({
        strength: match.strength,
        score: match.score,
        context: assessContext(group.meta, match.record, "pep"),
      }));
      group.aiItems = orderScreeningHits(group.aiItems, item => ({
        strength: item.result.nameAssessment?.strength || item.result.nameMatchStrength || "weak",
        score: pepNameScore(item.result),
        context: applyWebResearchStatus(assessContext(group.meta, item.result, "web"), item.result),
      }));
    }
    const visibleGroups = groups.filter(group => allPepSubjects.some(subject => normKey(subject.name) === normKey(group.name)));

    return visibleGroups.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.name.localeCompare(b.name);
    });
  }, [
    allPepSubjects,
    visibleMatches,
    visiblePepWebAssessment,
    pepWebAssessmentLoading,
    pepWebAssessmentErr,
    pepWebAssessmentSubjects,
  ]);

  function subjectHasSelectedEvidence(key, selectedKeys = relevantKeys) {
    const selected = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
    const group = groupedPepReviewItems.find(item => item.key === key);
    return !!group && (
      group.aiItems.some(candidate => selected.has(candidate.evidenceKey)) ||
      group.matches.some(match => selected.has(match._key))
    );
  }

  function contextReviewChanged(key, decisions = personDecisions) {
    const saved = decisions[key]?.contextFingerprint;
    return !!saved && (saved !== contextFingerprint(pepSubjects.find(s => s.key === key)?.meta) || decisions[key]?.contextRuleVersion !== CONTEXT_VERSION);
  }

  function isSubjectReviewed(key, decisions = personDecisions, selectedKeys = relevantKeys, notes = pep?.supportingInformation) {
    if (contextReviewChanged(key, decisions)) return false;
    return isSubjectReviewComplete(decisions[key]?.decision, subjectHasSelectedEvidence(key, selectedKeys), notes?.[key]);
  }

  const decidedCount = pepSubjects.filter(subject => isSubjectReviewed(subject.key)).length;
  const isReviewCompleted = !blockedSubjects.length && pepSubjects.length > 0 && decidedCount === pepSubjects.length;

  // Apply once per new screening run, so reopening or clearing an outcome
  // remains editable and is never immediately auto-selected again.
  useEffect(() => {
    if (readOnly || !autoClearRunRef.current || screeningLoading || pepWebAssessmentLoading) return;
    if (form?.internalUse?.pepScreening?.runAt !== autoClearRunRef.current) return;
    const keys = noMatchSubjectKeys("pep", pepSubjects.filter(subject => hasSubjectScreening(screeningResult, subject)), screeningResult, form?.internalUse?.pepWebAssessment);
    if (keys === null) return;
    autoClearRunRef.current = null;
    const next = { ...personDecisions };
    let changed = false;
    for (const key of keys) {
      if (next[key]?.decision || decisionEditKeys.has(key) || subjectHasSelectedEvidence(key)) continue;
      next[key] = {
        decision: "no_exposure",
        contextFingerprint: contextFingerprint(pepSubjects.find(subject => subject.key === key)?.meta),
        contextRuleVersion: CONTEXT_VERSION,
        decidedAt: new Date().toISOString(),
        decidedByUid: auth?.currentUser?.uid || "",
      };
      changed = true;
    }
    if (!changed) return;
    setPersonDecisions(next);
    persistScreeningState({ personReviews: next });
    updateReviewCompletion(next, relevantKeys, pep?.supportingInformation, true);
  });

  function renderDetails(rec) {
    const r = rec || {};
    const props = r?.properties || r?.entity?.properties || null;
    return renderPepDetailsFromProperties(props);
  }

  const staffDecision = clean(pep?.staffDecision) || "";
  function setStaffDecision(dec) {
    if (typeof writeAt !== "function") return;
    writeAt(["pep", "staffDecision"], dec);
  }

  const phase1 =
    staffDecision === "no_exposure"
      ? "no_exposure"
      : staffDecision
      ? "flagged"
      : "";
  const phase2 =
    staffDecision === "flagged_remediated"
      ? "flagged_remediated"
      : staffDecision === "flagged_confirmed"
      ? "flagged_confirmed"
      : "";

  function setPhase1(v) {
    if (!hasResult) return;
    if (!isReviewCompleted) return;
    if (v === "no_exposure") setStaffDecision("no_exposure");
    else setStaffDecision("flagged");
  }
  function setPhase2(v) {
    if (!hasResult) return;
    if (!isReviewCompleted) return;
    cancelDetailsCollapse();
    if (v === "flagged_remediated" || v === "flagged_confirmed") {
      if (staffDecision === v) {
        setStaffDecision("flagged");
        return;
      }
      setStaffDecision(v);
      scrollPepAnchorThenCollapse();
    }
  }

  // ---------------- CLIENT DECLARATION ----------------
  const clientDeclared = !!pep?.clientDeclared;

  const potentialCount = hasResult ? uniqueMatches.length + visiblePepWebAssessment.length : 0;
  const confirmedEmptyScreening = !blockedSubjects.length && hasResult && pepSubjects.length > 0 &&
    !screeningLoading && !pepWebAssessmentLoading && !screeningErr && !pepWebAssessmentErr &&
    noMatchSubjectKeys("pep", pepSubjects, screeningResult, form?.internalUse?.pepWebAssessment)?.length === pepSubjects.length;
  const exposureCount = pepSubjects.filter(
    (subject) => personDecisions[subject.key]?.decision === "exposure"
  ).length;

  const complianceUsable = hasResult && !screeningLoading && isReviewCompleted;
  const shouldHighlightComplianceAssessment =
    isReviewCompleted && exposureCount > 0 && !phase2;

  const subjectCounts = useMemo(() => {
    const rolesCount = {
      Client: 0,
      "Direct shareholder": 0,
      UBO: 0,
      "Authorised signatory": 0,
    };
    (pepSubjects || []).forEach((s) => {
      const roles = (Array.isArray(s?.roles) ? s.roles : [])
        .map((r) => clean(r))
        .filter(Boolean);
      const lower = roles.map((r) => r.toLowerCase());
      const normalizedRole = lower.some((r) => r.includes("client"))
        ? "Client"
        : lower.some((r) => r.includes("direct shareholder"))
        ? "Direct shareholder"
        : lower.some(
            (r) =>
              r === "ubo" ||
              r.includes("ubo") ||
              r === "settlor" ||
              r === "trustee" ||
              r === "beneficiary"
          )
        ? "UBO"
        : lower.some((r) => r.includes("authorised signatory"))
        ? "Authorised signatory"
        : "";
      if (!normalizedRole) return;
      rolesCount[normalizedRole] += 1;
    });
    return rolesCount;
  }, [pepSubjects]);

  // Public-web AI research rendered as an evidence item in the same card
  // design as the OpenSanctions hits (Stage 1). One row per person.
  function renderAiResearchRow(group, aiItem = null) {
    const evidenceKey = aiItem?.evidenceKey || `ai|${group.key}`;
    const relevant = relevantKeys.has(evidenceKey);
    const expanded = openKey === evidenceKey;
    const res = aiItem?.result || {};
    const assessmentText = !res.researchVersion && res.matchedProfileName
      ? (res.category === "pep" ? "Public sources identify a politically exposed person under this name." : "Public sources identify a relationship with a politically exposed person under this name.")
      : String(res.assessment || "").trim();
    const nameReason = pepNameReason(res, res.matchedProfileName);
    const webContext = applyWebResearchStatus(assessContext(group.meta, res, "web"), res);
    const webStrength = res.nameAssessment?.strength || res.nameMatchStrength || "weak";

    const researchIncompleteForSubject = pepWebAssessment.some(item =>
      item.subjectKey === group.key && item.result?.researchStatus === 'partial');

    if (!aiItem) {
      return (
        <div
          className="list-group-item"
          style={{
            border: "1px solid #DEE2E6",
            background: "#FBFBFB",
            color: "#837E7B",
          }}
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {pepWebAssessmentLoading ? (
              <span className="d-inline-flex align-items-center gap-2 small text-muted">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                Researching public web sources…
              </span>
            ) : researchIncompleteForSubject ? (
              <span className="small text-muted">
                Public-web findings need further review
              </span>
            ) : pepWebAssessmentErr ? (
              <span className="small text-muted">
                Public-web research is currently unavailable
              </span>
            ) : (
              <span className="small text-muted">
                {pepWebAssessment.some(item => item.subjectKey === group.key && Array.isArray(item.result?.candidates)) ? "No qualifying public-web findings" : "No public-web research available for this person."}
              </span>
            )}
          </div>
        </div>
      );
    }

    const identityLine = pepWebIdentityLine(res);
    const sources = Array.isArray(res.sources) ? res.sources.filter(Boolean) : [];

    return (
      <div
        className="list-group-item"
        style={{
          borderLeft: relevant ? "4px solid #EBE9E8" : "1px solid #DEE2E6",
          background: relevant ? "#FFFDFC" : "#FBFBFB",
          color: "#837E7B",
        }}
      >

        <div
          className="d-flex align-items-center justify-content-between gap-3"
          style={{ cursor: "pointer" }}
          onClick={() => setOpenKey(expanded ? null : evidenceKey)}
          role="button"
        >
          <div style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold" style={{ color: "#2f3441" }}>
                {res.matchedProfileName || "Public-web finding"}
              </div>
              {pepSignalBadge(pepWebClassification(res.category))}
              {res.category === "not_established" ? badge("PEP role not established", "grey") : overallPepMatchBadge(webStrength, webContext)}
              <ContextLimitation context={webContext} />
              {relevant ? badge("Selected", "grey") : null}
            </div>

            <div className="mt-1">
              {aiItem.previousResearch ? <div className="small text-muted mt-1" role="status">{previousPepResearchLabel(aiItem)}</div> : null}
              {nameReason ? (
                <div className="pepNameMatchNote mt-1">{nameReason}</div>
              ) : null}
              {assessmentText ? (
                <div
                  className="small text-muted mt-1"
                  style={{
                    display: expanded ? "block" : "-webkit-box",
                    opacity: 0.9,
                    overflow: expanded ? "visible" : "hidden",
                    whiteSpace: "pre-wrap",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: expanded ? "unset" : 6,
                  }}
                >
                  {assessmentText}
                </div>
              ) : (
                <div className="small text-muted mt-1">
                  No findings reported by the research run.
                </div>
              )}
              {identityLine ? (
                <div className="small text-muted mt-1" style={{ opacity: 0.9 }}>
                  {identityLine}
                </div>
              ) : null}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <button
              type="button"
              className="badge btn-lightBlue-outline"
              style={{
                fontWeight: 400,
                cursor: readOnly ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 26,
                padding: "0 10px",
              }}
              disabled={readOnly}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleRelevant(evidenceKey);
              }}
              title={relevant ? "Deselect this supporting hit" : "Select this supporting hit"}
              aria-pressed={relevant}
            >
              {relevant ? "Selected ✓" : "Select"}
            </button>

            {relevant ? (
              <i
                className="bi bi-bookmark-star-fill"
                style={{ color: "#798CA0" }}
              />
            ) : (
              <i className="bi bi-bookmark" style={{ color: "#798CA0" }} />
            )}


            <span className="text-muted" title="Show details">
              <i
                className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`}
              />
            </span>
          </div>
        </div>

        <IdentityContext context={webContext} strength={webStrength} nameReason={nameReason}
          screenedName={group.name} comparedName={res.matchedProfileName} />

        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.div key="details" {...detailsIn} style={{ overflow: "hidden" }}>
              <div
                className="mt-2 p-2 rounded-3 small"
                style={{ background: "#F6F4F2" }}
              >
                {sources.length ? (
                  <div className="mt-2">
                    <div className="fw-semibold" style={{ color: "#4C5465" }}>
                      Sources
                    </div>
                    <ul className="mb-0 ps-3">
                      {sources.map((src, i) => {
                        const url = typeof src === "string" ? src : src?.url || "";
                        const label =
                          (typeof src === "object" && (src?.title || src?.name)) ||
                          url;
                        if (!url) return null;
                        return (
                          <li key={`${url}_${i}`}>
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                color: "#869BB1",
                                textDecoration: "none",
                              }}
                            >
                              {label}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="mt-2 text-muted">No source links returned.</div>
                )}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  function renderPepMatchRow(m) {
    // Selecting a hit controls which evidence stays visible after review, without
    // changing decisions, risk or completion. Historical confirmations remain in the saved
    // dossier but are no longer editable per hit.
    const relevant = relevantKeys.has(m._key);
    const expanded = openKey === m._key;
    const niceReason = pepNameReason(m.record, m.name) || labelFromMatchType(m.matchType);
    const showReason = niceReason && niceReason !== "Potential match";
    const currentSubject = pepSubjects.find(subject => subject.key === m.subject?.key);
    const context = assessContext(currentSubject?.meta || m.subject?.meta || {}, m.record, "pep");
    const snap = summarizePepSnapshotFromRecord(m.record);
    const parts = [];
    if (snap.birthDate) parts.push(`Birth: ${snap.birthDate}`);
    if (snap.position) parts.push(`Position: ${snap.position}`);
    if (snap.notes) parts.push(`Notes: ${clampText(snap.notes, 140)}`);

    return (
      <div
        key={m._key}
        className="list-group-item"
        style={{
          borderLeft: relevant ? "4px solid #EBE9E8" : "1px solid #DEE2E6",
          background: relevant ? "#FFFDFC" : "#FBFBFB",
          color: "#837E7B",
        }}
      >

        <div
          className="d-flex align-items-center justify-content-between gap-3"
          style={{ cursor: "pointer" }}
          onClick={() => setOpenKey(expanded ? null : m._key)}
          role="button"
        >
          <div style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="fw-semibold" style={{ color: "#2f3441" }}>
                {m.name}
              </div>

              {pepHitClassificationBadge(m.classification)}
              {overallPepMatchBadge(m.strength, context)}
              <ContextLimitation context={context} />

              {relevant ? badge("Selected", "grey") : null}
            </div>

            <div className="mt-1">
              {showReason ? (
                <div className="pepNameMatchNote mt-1">{niceReason}</div>
              ) : null}

              {parts.length ? (
                <div
                  className="small text-muted mt-1"
                  style={{
                    display: "-webkit-box",
                    opacity: 0.9,
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 6,
                  }}
                >
                  {parts.join(" · ")}
                </div>
              ) : null}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <button
              type="button"
              className="badge btn-lightBlue-outline"
              style={{
                fontWeight: 400,
                cursor: readOnly ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 26,
                padding: "0 10px",
              }}
              disabled={readOnly}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleRelevant(m._key);
              }}
              title={relevant ? "Deselect this supporting hit" : "Select this supporting hit"}
              aria-pressed={relevant}
            >
              {relevant ? "Selected ✓" : "Select"}
            </button>

            {relevant ? (
              <i
                className="bi bi-bookmark-star-fill"
                style={{ color: "#798CA0" }}
              />
            ) : (
              <i className="bi bi-bookmark" style={{ color: "#798CA0" }} />
            )}


            <span className="text-muted" title="Show details">
              <i className={`bi ${expanded ? "bi-chevron-up" : "bi-chevron-down"}`} />
            </span>
          </div>
        </div>

        <IdentityContext context={context} strength={m.strength} nameReason={niceReason}
          screenedName={m.subject?.name} comparedName={m.name} />

        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.div key="details" {...detailsIn} style={{ overflow: "hidden" }}>
              <div className="mt-2 p-2 rounded-3" style={{ background: "#F6F4F2" }}>
                {renderDetails(m.record)}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="card kycCard mb-3" style={collapseReviewSnapshot ? { overflowAnchor: "none" } : undefined}>
      <div className="kycSectionHead position-relative">
        <h5 className="kycSectionTitle mb-0">{sectionNo} | PEP Exposure</h5>
      </div>

      <div className="kycSectionBody">
        {screeningErr ? (
          <div className="alert alert-danger mb-3">{screeningErr}</div>
        ) : null}

        {/* ----------- TOP SUMMARY CARD ----------- */}

        {/* background color top card overview main */}
        <div
          className="p-3 border rounded-3 mb-3"
          // style={{ background: "#C88294" }}
          style={{ background: "#EBECEF", cursor: readOnly ? "default" : "pointer" }}
          role={readOnly ? undefined : "button"}
          tabIndex={readOnly ? undefined : 0}
          onClick={readOnly ? undefined : toggleDetails}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleDetails();
                  }
                }
          }
        >
          {(() => {
            const clientStatusBadge = clientDeclared
              ? badge("Declared", "warning")
              : badge("Not declared", "success");
            const clientHint = clientDeclared
              ? "Client reports exposure"
              : "Client reports no exposure";

            const screeningStatusBadge = !hasResult
              ? badge("Pending", "grey")
              : screeningLoading || pepWebAssessmentLoading
              ? badge("Running", "warning")
              : potentialCount === 0 && confirmedEmptyScreening
              ? badge("No matches found", "success")
              : isReviewCompleted
              ? badge(
                  exposureCount > 0
                    ? `${exposureCount} PEP exposure`
                    : "Reviewed · No exposure",
                  exposureCount > 0 ? "danger" : "success"
                )
              : badge("Pending", "grey");

            const screeningHint = !hasResult
              ? "Screening not started"
              : isReviewCompleted
              ? pepSubjects.length === 1
                ? "Subject reviewed"
                : `All ${pepSubjects.length} subjects reviewed`
              : `${decidedCount} of ${allPepSubjects.length} ${pluralize(allPepSubjects.length, "subject", "subjects")} reviewed`;

            const complianceStatusBadge = !complianceUsable
              ? badge("Pending", "grey")
              : exposureCount === 0
              ? badge("PEP cleared", "success")
              : staffDecision === "flagged_remediated"
              ? badge("PEP cleared", "success")
              : staffDecision === "flagged_confirmed"
              ? badge("PEP confirmed", "danger")
              : badge("PEP identified", "warning");

            const complianceHint = !hasResult
              ? "Set outcome after screening"
              : !complianceUsable
              ? "Set outcome after review"
              : exposureCount === 0
              ? "No PEP exposure identified"
              : staffDecision === "flagged_remediated"
              ? "Exposure flagged · Remediated"
              : staffDecision === "flagged_confirmed"
              ? "Confirmed PEP identification"
              : "PEP identified · Pending decision";

            const overall = (() => {
              if (!complianceUsable) {
                return {
                  term: "Pending",
                  bg: "#F4F5F7",
                  border: "#D9DCE1",
                  icon: "bi-hourglass",
                  color: "#6B7C5F",
                  lottie: overallPendingAnim,
                };
              }
              if (exposureCount === 0) {
                return {
                  term: "Cleared",
                  bg: "#EAF7EE",
                  border: "#BFE6CC",
                  icon: "bi-check-circle",
                  color: "#6B7C5F",
                  lottie: overallClearedAnim,
                };
              }
              if (staffDecision === "flagged_confirmed") {
                return {
                  term: "PEP confirmed",
                  bg: "#FDEBEC",
                  border: "#F3B6BC",
                  icon: "bi-exclamation-circle-fill",
                  color: "#C88294",
                  lottie: overallAlertAnim,
                };
              }
              if (staffDecision === "flagged_remediated") {
                return {
                  term: "PEP cleared",
                  bg: "#EAF7EE",
                  border: "#BFE6CC",
                  icon: "bi-check-circle",
                  color: "#6B7C5F",
                  lottie: overallClearedAnim,
                };
              }
              return {
                term: "Pending",
                bg: "#F4F5F7",
                border: "#FFD79A",
                icon: "bi-hourglass",
                color: "#6B7C5F",
                lottie: overallPendingAnim,
              };
            })();

            return (
              <>
                <div
                  className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                  style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                >
                  <div>
                    <div
                      className="fw-semibold d-flex align-items-center gap-2"
                      style={{ color: "#2f3441" }}
                    >
                      {/* <i
                        className="bi bi-bank2 me-2"
                        style={{ color: "#A1A5AE", fontSize: 25 }}
                      /> */}
                      <span
                        style={{
                          width: 40,
                          height: 40,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        <Lottie
                          animationData={pepAnim}
                          loop
                          autoplay
                          style={{ width: 40, height: 40 }}
                        />
                      </span>
                      PEP overview
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center gap-2"
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {(hasResult || screeningLoading) && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                        style={{ height: 30, width: 30 }}
                        aria-label={screeningLoading ? "Screening in progress" : "Re-run screening"}
                        disabled={readOnly || screeningLoading || !pepSubjects.length}
                        onClick={(e) => {
                          e.stopPropagation();
                          runPepScreening();
                        }}
                      >
                        <i className="bi bi-arrow-repeat" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDetails();
                      }}
                      disabled={readOnly}
                      style={{ height: 30 }}
                    >
                      {showDetails ? (
                        <>
                          <i className="bi bi-chevron-up me-1" />
                          Hide details
                        </>
                      ) : (
                        <>
                          <i className="bi bi-chevron-down me-1" />
                          Show details
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {/* <div
                  className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                  style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                >
                  <div>
                    <div
                      className="fw-semibold d-flex align-items-center gap-2"
                      style={{ color: "#2f3441" }}
                    >
                      <i
                        className="bi bi-bank2 me-2"
                        style={{ color: "#A1A5AE", fontSize: 25 }}
                      />
                      PEP overview
                    </div>
                  </div>
                </div> */}

                <hr className="my-3" />

                <div className="row g-2 pepTilesGrid">
                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-person-check" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              id="fi_8703172"
                              height="512"
                              viewBox="0 0 60 60"
                              width="512"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill="currentColor"
                                d="m34.82 8.06c-5.71 0-10.35 4.64-10.35 10.35s4.64 10.35 10.35 10.35 10.35-4.64 10.35-10.35-4.65-10.35-10.35-10.35zm0 18.69c-4.6 0-8.35-3.74-8.35-8.35s3.74-8.35 8.35-8.35c4.6 0 8.35 3.74 8.35 8.35s-3.75 8.35-8.35 8.35z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="m22.54 38.64c.66.61 1.04 1.49 1.04 2.41v9.3c0 .55.45 1 1 1s1-.45 1-1v-9.3c0-1.47-.61-2.88-1.68-3.88-5.42-5.04-10.5-12.12-15.54-21.67-.73-1.39-.32-3.14.95-4.07.75-.54 1.7-.73 2.6-.52s1.66.8 2.09 1.61c3.05 5.79 6.49 11.08 10.21 15.73 1.83 2.28 4.56 3.58 7.49 3.58h9.16c6.14 0 11.14 5 11.14 11.14v7.37c0 .55.45 1 1 1s1-.45 1-1v-7.37c0-7.25-5.89-13.14-13.14-13.14h-9.16c-2.33 0-4.49-1.03-5.93-2.83-3.64-4.54-7.01-9.73-10-15.41-.7-1.33-1.94-2.29-3.4-2.63s-3-.03-4.23.85c-2.08 1.51-2.74 4.35-1.55 6.62 5.15 9.76 10.36 17.02 15.95 22.21z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="m46 43.71v7.23c0 .55.45 1 1 1s1-.45 1-1v-7.23c0-.55-.45-1-1-1s-1 .44-1 1z"
                              ></path>
                            </svg>
                          </span>
                          <div className="fw-semibold small">
                            Client declaration
                          </div>
                        </div>
                        {clientStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">{clientHint}</div>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-search" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              height="512"
                              viewBox="0 0 64 64"
                              width="512"
                              xmlns="http://www.w3.org/2000/svg"
                              id="fi_6772342"
                            >
                              <g
                                id="_10-Security_Gate"
                                data-name="10-Security Gate"
                              >
                                <path
                                  fill="currentColor"
                                  d="m53 2h-42a.99974.99974 0 0 0 -1 1v58a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-43h32v43a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-58a.99974.99974 0 0 0 -1-1zm-39 58h-2v-56h2zm34-44h-32v-12h32zm4 44h-2v-56h2zm-26-46h12a.99974.99974 0 0 0 1-1v-6a.99974.99974 0 0 0 -1-1h-12a.99974.99974 0 0 0 -1 1v6a.99974.99974 0 0 0 1 1zm1-6h10v4h-10zm-9 0a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm23-4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm-4 20h-.54047a6 6 0 1 0 -8.91906 0h-.54047a7.00818 7.00818 0 0 0 -7 7v6a3.00328 3.00328 0 0 0 3 3h2v11a3.00328 3.00328 0 0 0 3 3h8a3.00328 3.00328 0 0 0 3-3v-11h2a3.00328 3.00328 0 0 0 3-3v-6a7.00818 7.00818 0 0 0 -7-7zm-5-8a4 4 0 1 1 -4 4 4.00427 4.00427 0 0 1 4-4zm10 21a1.001 1.001 0 0 1 -1 1h-2v-5a1 1 0 0 0 -2 0v18a1.001 1.001 0 0 1 -1 1h-3v-9a1 1 0 0 0 -2 0v9h-3a1.001 1.001 0 0 1 -1-1v-18a1 1 0 0 0 -2 0v5h-2a1.001 1.001 0 0 1 -1-1v-6a5.00588 5.00588 0 0 1 5-5h10a5.00588 5.00588 0 0 1 5 5zm-34-13a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-4.55469-9.168a1 1 0 1 1 1.10938-1.664l3 2a1 1 0 1 1 -1.10938 1.664zm4.38669 16.61331a.9993.9993 0 0 1 -.27731 1.38669l-3 2a1 1 0 0 1 -1.10938-1.664l3-2a.99886.99886 0 0 1 1.38669.27731zm54.168-7.44531a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-5.832-7.44531a.9993.9993 0 0 1 .27734-1.38672l3-2a1 1 0 1 1 1.10935 1.66403l-3 2a.99992.99992 0 0 1 -1.38672-.27734zm4.38669 16.61331a1 1 0 1 1 -1.10938 1.664l-3-2a1 1 0 0 1 1.10938-1.664z"
                                ></path>
                              </g>
                            </svg>
                          </span>
                          <div className="fw-semibold small">Screening</div>
                        </div>
                        {screeningStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">
                        {screeningHint}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="p-2 rounded-3 border bg-white h-100">
                      <div
                        className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
                        style={{ marginTop: 3 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* <i className="bi bi-clipboard-check" /> */}
                          <span
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 22,
                              height: 22,
                              color: "#817672",
                              flex: "0 0 auto",
                            }}
                            aria-hidden="true"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              id="fi_14979987"
                              viewBox="0 0 60 60"
                            >
                              <path
                                fill="currentColor"
                                d="M36.949,31.846A3.005,3.005,0,0,0,39,29V26a9,9,0,0,0-18,0v3a3.005,3.005,0,0,0,2.051,2.846A7,7,0,0,0,26,36.734v1.5l-3.9,1.053a7.242,7.242,0,0,0-5.228,4.542L15.6,47.4l-2.4-1.917a1.579,1.579,0,0,0-.2-.13V42.68a2.685,2.685,0,0,0-5.29-.642l-.007.026a4.629,4.629,0,0,0-4.211,1.453,1.892,1.892,0,0,0,.617,2.995,19.054,19.054,0,0,1,3.045,1.7c.465.336.843.633,1.214.935a1.83,1.83,0,0,0,.651,1.2l6.43,4.77a3.45,3.45,0,0,0,3.086.8,3.406,3.406,0,0,0,2.415-1.958L22,51.992V58a2,2,0,0,0,2,2H36a2,2,0,0,0,2-2V51.987l1.013,1.892a3.456,3.456,0,0,0,2.452,2.036,3.4,3.4,0,0,0,3.016-.744l6.568-4.882a1.769,1.769,0,0,0,.583-1.14c.371-.3.747-.6,1.207-.933a19.142,19.142,0,0,1,3.055-1.706,1.891,1.891,0,0,0,.6-3,4.641,4.641,0,0,0-4.2-1.442l-.007-.029A2.685,2.685,0,0,0,47,42.68v2.668a1.718,1.718,0,0,0-.2.131l-2.326,1.937-1.345-3.588a7.219,7.219,0,0,0-5.184-4.53L34,38.234v-1.5A7,7,0,0,0,36.949,31.846ZM30,19a7.009,7.009,0,0,1,7,7v.179c-.037-.013-.072-.032-.11-.044a3.073,3.073,0,0,0-4.626-1.743,4.521,4.521,0,0,1-4.527,0,3.075,3.075,0,0,0-4.629,1.743c-.037.011-.071.03-.108.043V26A7.009,7.009,0,0,1,30,19ZM24,30a1,1,0,0,1,0-2,1.081,1.081,0,0,0,1-1.075,1.031,1.031,0,0,1,.564-.9,1.1,1.1,0,0,1,.51-.129,1.023,1.023,0,0,1,.564.169,6.5,6.5,0,0,0,6.723,0,1.041,1.041,0,0,1,1.073-.04A1.064,1.064,0,0,1,35,27a1,1,0,0,0,1,1,1,1,0,0,1,0,2,1,1,0,0,0-1,1,5,5,0,0,1-10,0A1,1,0,0,0,24,30Zm9.439,10.155,1.921.517a6.768,6.768,0,0,1-2.036,2.614.392.392,0,0,1-.365.049.38.38,0,0,1-.251-.261,5.694,5.694,0,0,0-.793-1.654A4.831,4.831,0,0,0,33.439,40.155ZM31.984,52.646,30,53.965l-1.984-1.319L28.862,47h2.276ZM28.721,45l-.116-.348a2.294,2.294,0,0,0,.6-1.006A3.239,3.239,0,0,1,30,42.215a3.2,3.2,0,0,1,.788,1.417,2.323,2.323,0,0,0,.608,1.019L31.279,45Zm-1.433-1.911a.373.373,0,0,1-.247.246.4.4,0,0,1-.37-.052,6.8,6.8,0,0,1-2.034-2.609l1.924-.519a4.851,4.851,0,0,0,1.525,1.266A5.738,5.738,0,0,0,27.288,43.089ZM5.075,44.754a2.634,2.634,0,0,1,2.935-.522,1,1,0,0,0,1.38-.669l.259-1.037A.7.7,0,0,1,10.32,42a.681.681,0,0,1,.68.68v2.769a1.8,1.8,0,0,0-.233.205l-1.524,1.63c-.28-.221-.581-.451-.924-.7A20.843,20.843,0,0,0,5.075,44.754ZM49.68,42a.7.7,0,0,1,.671.522l.259,1.041a1,1,0,0,0,1.38.669,2.69,2.69,0,0,1,2.962.509,20.887,20.887,0,0,0-3.278,1.849c-.339.245-.637.473-.916.693l-1.525-1.629A1.8,1.8,0,0,0,49,45.449V42.68A.681.681,0,0,1,49.68,42Zm-8.424,2.53L42.6,48.118a1.956,1.956,0,0,0,3.052.84l2.248-1.8,1.647,1.759-6.327,4.7a1.478,1.478,0,0,1-1.321.342,1.542,1.542,0,0,1-1.087-.949l-2.931-5.486A1,1,0,0,0,36,48V58H24V48a1,1,0,0,0-1.882-.472l-2.969,5.564a1.481,1.481,0,0,1-1.049.871,1.532,1.532,0,0,1-1.389-.4L10.45,48.921,12.1,47.159l2.248,1.8a1.955,1.955,0,0,0,3.052-.84l1.345-3.586a5.271,5.271,0,0,1,3.832-3.3l.1-.027a9.027,9.027,0,0,0,2.805,3.692,2.356,2.356,0,0,0,1.241.443l.25.75-.956,6.388a1.828,1.828,0,0,0,.794,1.774L28.989,55.7a1.823,1.823,0,0,0,2.022,0l2.176-1.446a1.831,1.831,0,0,0,.793-1.781l-.955-6.381.247-.742a2.359,2.359,0,0,0,1.239-.449,8.969,8.969,0,0,0,2.806-3.7l.151.04A5.247,5.247,0,0,1,41.256,44.53ZM32,38.743a2.944,2.944,0,0,1-2,1.243,2.946,2.946,0,0,1-2-1.241V37.706a6.95,6.95,0,0,0,4,0Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M11,22A11,11,0,1,0,0,11,11.013,11.013,0,0,0,11,22ZM11,2a9,9,0,1,1-9,9A9.01,9.01,0,0,1,11,2Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M49,0A11,11,0,1,0,60,11,11.013,11.013,0,0,0,49,0Zm0,20a9,9,0,1,1,9-9A9.01,9.01,0,0,1,49,20Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M7.293,14.707a1,1,0,0,0,1.414,0L11,12.414l2.293,2.293a1,1,0,0,0,1.414-1.414L12.414,11l2.293-2.293a1,1,0,1,0-1.414-1.414L11,9.586,8.707,7.293A1,1,0,1,0,7.293,8.707L9.586,11,7.293,13.293A1,1,0,0,0,7.293,14.707Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M51.168,7.445l-3.323,4.986-1.138-1.138a1,1,0,0,0-1.414,1.414l2,2A1,1,0,0,0,48,15c.032,0,.065,0,.1-.005a1,1,0,0,0,.733-.44l4-6a1,1,0,0,0-1.664-1.11Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M50,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                              ></path>
                              <path
                                fill="currentColor"
                                d="M12,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                              ></path>
                            </svg>
                          </span>
                          <div className="fw-semibold small">Assessment</div>
                        </div>
                        {complianceStatusBadge}
                      </div>
                      <div className="small text-muted mt-1">
                        {complianceHint}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-2">
                    <div
                      className="p-2 rounded-3 border h-100 d-flex align-items-center justify-content-center text-center"
                      style={{
                        background: overall.bg,
                        borderColor: overall.border,
                      }}
                    >
                      <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                        <span
                          style={{
                            width: 35,
                            height: 35,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            lineHeight: 1,
                          }}
                          aria-hidden="true"
                        >
                          <Lottie
                            animationData={overall.lottie}
                            loop
                            autoplay
                            style={{ width: 35, height: 35 }}
                          />
                        </span>
                        {/* <i
                          className={`bi ${overall.icon}`}
                          style={{
                            fontSize: 24,
                            lineHeight: 1,
                            color: overall.color,
                          }}
                          aria-hidden="true"
                        /> */}
                        <div
                          className="fw-semibold small"
                          style={{ color: overall.color, fontSize: 12 }}
                        >
                          {overall.term}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Extensions: card — arrow — card, rendered inside the overview band.
              Overflow is released after the height animation so the arrow can
              escape the band and land in the screening card below. */}
          <AnimatePresence initial={false} onExitComplete={() => finishCollapseScroll("extensions")}>
            {showDetails ? (
              <motion.div
                key="pep_ext_row"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={fullDetailsExit}
                transition={T.med}
                onAnimationStart={() => setExtRevealed(false)}
                onAnimationComplete={() => setExtRevealed(!!showDetails)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                style={{
                  cursor: "default",
                  overflow: extRevealed ? "visible" : "hidden",
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className="row g-2 pepTilesGrid" style={{ paddingTop: 8 }}>
                {/* 1) Client declaration — same idea as the middle: a short white
                    3D arrow drops from the tile above INTO this card (card itself flat) */}
                <div className="col-12 col-md d-flex">
                  <div className="w-100 position-relative" style={{ paddingTop: 0 }}>

                  <div
                    className="border rounded-3 h-100 d-flex flex-column w-100"
                    style={{ padding: "16px", background: "#F9F9F9" }}
                  >
                    <div className="mt-0 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                      <div
                        className="text-muted small"
                        style={{ fontWeight: 500 }}
                      >
                        PEP declared by client?
                      </div>

                      <div
                        className="btn-group"
                        role="group"
                        aria-label="Client declaration"
                      >
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            clientDeclared
                              ? "btn-toggle-off-lightgrey"
                              : "btn-toggle-on-lightgrey"
                          }`}
                          disabled={readOnly}
                          onClick={() =>
                            writeAt?.(["pep", "clientDeclared"], false)
                          }
                        >
                          No
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            clientDeclared
                              ? "btn-toggle-on-lightgrey"
                              : "btn-toggle-off-lightgrey"
                          }`}
                          disabled={readOnly}
                          onClick={() =>
                            writeAt?.(["pep", "clientDeclared"], true)
                          }
                        >
                          Yes
                        </button>
                      </div>

                    </div>

                    <div
                      className="mt-3"
                      style={{
                        background: "#FCFCFC",
                        border: "1px solid #EFECE8",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <div
                        className="text-muted small"
                        style={{ marginTop: 0, marginBottom: 8 }}
                      >
                        Client comments
                      </div>
                      <textarea
                        ref={clientCommentsRef}
                        className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                          "pep",
                          "clientDetails",
                        ])}`}
                        rows={1}
                        disabled={readOnly}
                        value={pep?.clientDetails || ""}
                        onChange={(e) => {
                          const el = e.currentTarget;
                          writeAt?.(["pep", "clientDetails"], e.target.value);
                          window.requestAnimationFrame(() =>
                            autoResizeTextarea(el)
                          );
                        }}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        placeholder="No comments"
                      />
                    </div>
                  </div>
                  </div>
                </div>

                {/* 2) Screening connector — a wide arrow pointing into the screening card below */}
                <div className="col-12 col-md d-flex">
                  {/* Relative shell keeps the layout box; the arrow itself extends
                      28px past it so the tip enters the screening card below. */}
                  <div className="w-100 position-relative">
                  {/* drop-shadow lives on this un-clipped wrapper so it traces the
                      arrow silhouette and shows the tip floating inside the card below */}
                  <motion.div
                    animate={
                      !showDetails && !collapseReviewSnapshot
                        ? // hide: ride up through the top fade-mask with the collapse
                          { opacity: 1, y: -160 }
                        : extRevealed
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: -8 }
                    }
                    transition={T.med}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: -100,
                      zIndex: 2,
                      filter: "drop-shadow(0 2px 3px rgba(76, 84, 101, 0.10))",
                      WebkitMaskImage:
                        "linear-gradient(to bottom, transparent 0px, #000 50px)",
                      maskImage:
                        "linear-gradient(to bottom, transparent 0px, #000 50px)",
                    }}
                  >
                  <div
                    className="d-flex flex-column"
                    style={{ height: "calc(100% - 14px)", width: "90%", margin: "0 auto" }}
                  >
                    <div
                      className="d-flex flex-column align-items-center text-center flex-grow-1 position-relative"
                      style={{
                        width: "100%",
                        marginBottom: -2,
                        position: "relative",
                        zIndex: 1,
                        background: "#EDEAE9",
                        border: "0.5px solid #DCD3C7",
                        borderBottom: "none",
                        borderRadius: 0,
                        padding: "16px 12px 8px",
                      }}
                    >
                      <div
                        className="flex-grow-1 d-flex flex-column align-items-center justify-content-center"
                        style={{ gap: 10 }}
                      >
                        {screeningLoading ? (
                          <>
                            <div
                              className="appDotsLoader"
                              aria-hidden="true"
                              style={{ transform: "scale(0.6)", transformOrigin: "center" }}
                            />
                            <div style={{ color: "#A5ABB5", fontSize: 12, fontWeight: 500 }}>
                              Screening...
                            </div>
                          </>
                        ) : !hasResult ? (
                          <>
                            <div className="small text-muted">
                              {pepSubjects?.length ? (
                                <>
                                  Ready to screen {pepSubjects.length} name
                                  {pepSubjects.length > 1 ? "s" : ""}
                                </>
                              ) : (
                                "Missing name(s)"
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary kycPepSearchBtn"
                              style={{
                                width: 150,
                                minHeight: 38,
                                padding: "10px 18px",
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 3,
                              }}
                              onClick={runPepScreening}
                              disabled={readOnly || !pepSubjects?.length}
                              title={customerName ? "Run screening" : "Missing name"}
                            >
                              <img
                                src={doubleTapFullIcon}
                                alt=""
                                aria-hidden="true"
                                style={{ width: 32, height: 32, flex: "0 0 auto" }}
                              />
                              Search PEP
                            </button>
                          </>
                        ) : (
                          <div style={{ paddingTop: 34 }}>
                              {pepSubjects?.length ? (
                                <div
                                  className="small text-muted mt-2"
                                  style={{ opacity: 0.9 }}
                                >
                                  Screening results
                                  <div style={{ fontSize: 11, color: "#9298A1", marginTop: 2 }}>
                                    {pepSubjects.length}{" "}
                                    {pluralize(pepSubjects.length, "subject", "subjects")}
                                    {" · "}
                                    {screenedRolesText(pepSubjects)}
                                  </div>
                                </div>
                              ) : null}

                              {isReviewCompleted ? (
                                <div style={{ fontSize: 22, color: "#B7B9BF", marginTop: 8 }}>
                                  <span role="img" aria-label="Review completed">✓</span>
                                </div>
                              ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                    <svg
                          width="100%"
                          height="60"
                          viewBox="0 0 400 30"
                          preserveAspectRatio="none"
                          style={{ display: "block", flex: "0 0 auto" }}
                          aria-hidden="true"
                        >
                          <path
                            d="M0 0 H400 V3 Q400 12 376 15.8 L200 30 L24 15.8 Q0 12 0 3 Z"
                            fill="#EDEAE9"
                          />
                          {/* outline drawn separately so the head's TOP edge
                              (the shaft junction) carries no stroke at all */}
                          <path
                            d="M400 0 V3 Q400 12 376 15.8 L200 30 L24 15.8 Q0 12 0 3 V0"
                            fill="none"
                            stroke="#DCD3C7"
                            strokeWidth={0.5}
                            vectorEffect="non-scaling-stroke"
                          />
                        </svg>
                  </div>
                  </motion.div>
                  </div>
                </div>

                {/* 3) Compliance assessment — same white 3D arrow dropping from
                    the Assessment tile into the card; card starts below the arrow.
                    flexBasis adds the Overall tile's 2/12 so the card also spans
                    below the PEP confirmed tile. */}
                <div className="col-12 col-md d-flex pepExtAssessCol">
                  <div className="w-100 position-relative" style={{ paddingTop: 0 }}>
                  <div
                    className={`kycPepComplianceAssessmentCard border rounded-3 h-100 d-flex flex-column w-100 position-relative ${
                      complianceAssessmentGlowActive
                        ? "kycComplianceAssessmentGlow"
                        : ""
                    }`}
                    onAnimationEnd={(e) => {
                      if (e.target !== e.currentTarget) return;
                      setComplianceAssessmentGlowActive(false);
                      setComplianceAssessmentIntroBackgroundActive(false);
                    }}
                    style={
                      !complianceUsable || decisionEditKeys.size > 0 || (exposureCount === 0 && !SHOW_NO_EXPOSURE_CARD)
                        ? { padding: "16px", visibility: "hidden", pointerEvents: "none" }
                        : shouldHighlightComplianceAssessment
                        ? {
                            padding: "16px",
                            background:
                              complianceAssessmentIntroBackgroundActive
                                ? "#FFFEF8"
                                : "#F9F9F9",
                            borderColor: "#DCD0A0",
                            boxShadow:
                              "inset 0 0 0 1px rgba(220, 208, 160, 0.18), 0 0 10px rgba(126, 111, 60, 0.06)",
                            transition:
                              "background-color 0.65s ease, box-shadow 0.25s ease",
                          }
                        : { padding: "16px", background: "#F9F9F9" }
                    }
                  >
                    {exposureCount === 0 ? (
                      <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                        <span
                          className="badge badgeStandard"
                          style={{ background: "transparent", color: "#A5ABB5", border: "1px solid #D9DCE1" }}
                        >
                          No exposure flagged
                        </span>
                      </div>
                    ) : (
                    <>
                    {shouldHighlightComplianceAssessment &&
                      actionRequiredBadgeVisible && (
                      <div className="kycPepActionRequiredOverlay">
                      <span
                        className="d-inline-flex flex-column align-items-center justify-content-center kycActionRequiredBadge"
                        title="Action required"
                        aria-label="Action required"
                        style={{
                          width: 70,
                          height: 70,
                          border: "1px solid #DCD0A0",
                          borderRadius: "50%",
                          padding: "7px 6px 8px",
                          gap: 2,
                          fontSize: 8,
                          fontWeight: 500,
                          lineHeight: 1,
                          color: "#6C757D",
                          cursor: "default",
                          textAlign: "center",
                          userSelect: "none",
                          zIndex: 1,
                        }}
                      >
                        <img
                          src={handFingerUpIcon}
                          alt=""
                          aria-hidden="true"
                          style={{
                            width: 22,
                            height: 22,
                            display: "block",
                            flex: "0 0 auto",
                          }}
                        />
                        <span
                          style={{
                            display: "block",
                            width: "100%",
                            marginTop: 2,
                          }}
                        >
                          Action required
                        </span>
                      </span>
                      </div>
                    )}
                    <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                      {complianceUsable ? (
                        <div className="w-100 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <div
                            className="small text-muted"
                            style={{ fontWeight: 500 }}
                          >
                            {"Final assessment of identified exposure :"}
                          </div>
                          <div className="btn-group flex-wrap" role="group">
                                <button
                                  type="button"
                                  className={`btn btn-sm ${
                                    phase2 === "flagged_remediated"
                                      ? "btn-toggle-on-lightgrey"
                                      : "btn-toggle-off-lightgrey"
                                  } ${cc(["pep", "staffDecision"])}`}
                                  disabled={readOnly}
                                  onClick={() =>
                                    setPhase2("flagged_remediated")
                                  }
                                >
                                  Remediated
                                </button>

                                <button
                                  type="button"
                                  className={`btn btn-sm ${
                                    phase2 === "flagged_confirmed"
                                      ? "btn-toggle-on-lightgrey"
                                      : "btn-toggle-off-lightgrey"
                                  } ${cc(["pep", "staffDecision"])}`}
                                  disabled={readOnly}
                                  onClick={() => setPhase2("flagged_confirmed")}
                                >
                                  Confirmed
                                </button>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={
                        !complianceUsable
                          ? { visibility: "hidden", pointerEvents: "none" }
                          : undefined
                      }
                      className="flex-grow-1 d-flex flex-column"
                    >

                      <div
                        className="mt-3"
                        style={{
                          background: "#FCFCFC",
                          border: "1px solid #EFECE8",
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <div
                          className="text-muted small"
                          style={{ marginTop: 0, marginBottom: 8 }}
                        >
                          Compliance comments
                        </div>
                        <textarea
                          ref={complianceCommentsRef}
                          className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                            "pep",
                            "staffAssessment",
                          ])}`}
                          rows={1}
                          disabled={readOnly}
                          value={pep?.staffAssessment || ""}
                          onChange={(e) => {
                            const el = e.currentTarget;
                            writeAt?.(
                              ["pep", "staffAssessment"],
                              e.target.value
                            );
                            window.requestAnimationFrame(() =>
                              autoResizeTextarea(el)
                            );
                          }}
                          onInput={(e) => autoResizeTextarea(e.currentTarget)}
                          placeholder="No comments"
                        />
                      </div>
                    </div>

                    </>
                    )}

                  </div>
                  </div>
                </div>

                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Screening results card — outside the band; the arrow's tip lands inside it */}
                {/* 4) Screening results card (✅ animated entrance + layout) */}
                <AnimatePresence initial={false} mode="wait" onExitComplete={() => finishCollapseScroll("results")}>
                  {showDetails && screeningLoading && !runningSubjectKey ? (
                    <motion.div
                      key="pep-screening-placeholder"
                      className="p-3 border rounded-3 mb-3"
                      role="status"
                      aria-label="Preparing screening results"
                      aria-busy="true"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      style={{ marginTop: 8, position: "relative", zIndex: 0, isolation: "isolate" }}
                    >
                      <div
                        className="rounded-3 border p-3 placeholder-glow"
                        aria-hidden="true"
                        style={{ background: "#F9F7F5", color: "#D9DDE2" }}
                      >
                        <span className="placeholder d-block rounded mb-2" style={{ width: "25%", height: 14 }} />
                        <span className="placeholder d-block rounded mb-3" style={{ width: "12%", height: 10 }} />
                        <div className="rounded-3 border p-3" style={{ background: "#FCFBFA" }}>
                          <span className="placeholder d-block rounded mb-3" style={{ width: "35%", height: 12 }} />
                          <span className="placeholder d-block rounded mb-2" style={{ width: "85%", height: 10 }} />
                          <span className="placeholder d-block rounded" style={{ width: "65%", height: 10 }} />
                        </div>
                      </div>
                    </motion.div>
                  ) : showDetails && allPepSubjects.length > 0 ? (
                    <motion.div
                      key="pep-screening-card"
                      // Keep sticky subject headers below the overview connector arrow (z-index 2).
                      style={{ marginTop: 8, position: "relative", zIndex: 0, isolation: "isolate" }}
                      {...cardIn}
                      animate={{ ...cardIn.animate, height: "auto" }}
                      exit={fullDetailsExit}
                      layout="position"
                    >
                      <div
                        className="p-3 border rounded-3 mb-3"
                        // F5F3F2 FCFCFF
                      >
                        {hasResult && !hideScreeningSummary && <div
                          className="p-3 rounded-3 border"
                          style={{
                            background: styles.bg,
                            borderColor: styles.border,
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                            <div>
                              {Array.isArray(screeningResult?.errors) &&
                              screeningResult.errors.length ? (
                                <div
                                  className="small text-muted mt-1"
                                  style={{ color: "#a36b6b" }}
                                >
                                  {screeningResult.errors.length} name
                                  {screeningResult.errors.length > 1
                                    ? "s"
                                    : ""}{" "}
                                  failed to screen (still showing results for
                                  other names).
                                </div>
                              ) : null}
                            </div>

                            <div className="d-flex flex-column align-items-end gap-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={runPepScreening}
                                  disabled={readOnly || screeningLoading}
                                  title="Re-run screening"
                                >
                                  <i className="bi bi-arrow-repeat" />
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={clearScreening}
                                  disabled={readOnly || screeningLoading}
                                  title="Clear results"
                                >
                                  <i className="bi bi-x-lg" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        }

                        {groupedPepReviewItems.length ? (
                          <>
                            <div
                              className={hideScreeningSummary ? undefined : "mt-3"}
                              onScroll={(event) => {
                                if (!pepReviewHeaderSticky) {
                                  setPepReviewScrolled(false);
                                  return;
                                }
                                const next = event.currentTarget.scrollTop > 2;
                                setPepReviewScrolled((prev) =>
                                  prev === next ? prev : next
                                );
                              }}
                              style={{
                                maxHeight: 560,
                                overflowY: "auto",
                              }}
                            >
                              <motion.div
                                className="d-grid"
                                style={{ gap: 12 }}
                                layout="position"
                                transition={T.med}
                              >
                                <AnimatePresence initial={false}>
                                  {groupedPepReviewItems.map((group, idx) => {
                                    if (needsFullName(group)) return (
                                      <ScreeningNameNotice key={group.key} subjects={[group]} readOnly={readOnly} />
                                    );
                                    const subjectWasScreened = hasSubjectScreening(screeningResult, group) ||
                                      (hasResult && group.matches.length > 0) || (hasResult && group.aiItems.length > 0);
                                    if (!subjectWasScreened) return <ScreeningPendingSubject key={group.key} subject={group} readOnly={readOnly}
                                      disabled={screeningLoading || pepWebAssessmentLoading} loading={runningSubjectKey === group.key}
                                      onRun={() => runPepScreening(group)} />;

                                    const aiItem = group.aiItem;
                                    const personDecision =
                                      personDecisions[group.key]?.decision;
                                    const decisionEditing =
                                      decisionEditKeys.has(group.key) || contextReviewChanged(group.key);
                                    const showDecisionChip =
                                      !!personDecision && !decisionEditing;
                                    const evidenceDecision = collapseReviewSnapshot
                                      ? collapseReviewSnapshot.personDecisions[group.key]?.decision
                                      : personDecision;
                                    const evidenceCollapsed = collapseReviewSnapshot
                                      ? !!evidenceDecision && !collapseReviewSnapshot.decisionEditKeys.has(group.key)
                                      : showDecisionChip;
                                    const hasSelectedEvidence =
                                      group.aiItems.some(candidate => relevantKeys.has(candidate.evidenceKey)) ||
                                      group.matches.some((match) => relevantKeys.has(match._key));
                                    const needsSupportingReason =
                                      evidenceDecision === "exposure" && !hasSelectedEvidence;
                                    const visibleWebCandidates = evidenceCollapsed
                                      ? group.aiItems.filter(candidate => relevantKeys.has(candidate.evidenceKey))
                                      : group.aiItems;
                                    const needsWebResearch = clean(group.meta?.subjectType).toLowerCase() !== "entity";
                                    const webResearchIncomplete = pepWebAssessment.some(item => item.subjectKey === group.key && item.result?.researchStatus === 'partial');
                                    const webResearchComplete = !needsWebResearch || (!webResearchIncomplete && pepWebAssessment.some(item => item.subjectKey === group.key && item.result));
                                    const showPublicWebEvidence = visibleWebCandidates.length > 0 || (needsWebResearch && !evidenceCollapsed && (!webResearchComplete || pepWebAssessmentLoading || pepWebAssessmentErr));
                                    // Empty findings belong to this subject; another subject's
                                    // pending or failed public-web research must not hide them.
                                    const noPepFindings = !screeningLoading &&
                                      noMatchSubjectKeys("pep", [group], screeningResult, { items: pepWebAssessment })?.includes(group.key) &&
                                      group.aiItems.length === 0 && group.matches.length === 0;
                                    const visiblePersonMatches = evidenceCollapsed
                                      ? group.matches.filter((match) =>
                                          relevantKeys.has(match._key)
                                        )
                                      : group.matches;
                                    const signalLabel =
                                      consolidatedPepSignalLabel(
                                        aiItem,
                                        group.matches
                                      );
                                    const contextIcon = pepWebContextIcon(
                                      aiItem?.result?.outcome
                                    );

                                    return (
                                      <motion.div
                                        key={group.key}
                                        className="rounded-3"
                                        layout="position"
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{
                                          opacity: 1,
                                          y: 0,
                                          transition: {
                                            ...T.fast,
                                            delay: Math.min(idx, 8) * 0.015,
                                          },
                                        }}
                                        exit={{ opacity: 0, y: 4, transition: T.fast }}
                                        style={{
                                          background: "#F9F7F5",
                                          color: "#4C5465",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: pepReviewHeaderSticky
                                              ? "sticky"
                                              : "relative",
                                            top: 0,
                                            zIndex: pepReviewHeaderSticky ? 3 : 1,
                                            background: "transparent",
                                          }}
                                        >
                                          {pepReviewHeaderSticky ? (
                                            <>
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  position: "absolute",
                                                  top: 0,
                                                  left: 0,
                                                  width: 18,
                                                  height: 18,
                                                  background: "#fff",
                                                  pointerEvents: "none",
                                                  zIndex: 0,
                                                }}
                                              />
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  position: "absolute",
                                                  top: 0,
                                                  right: 0,
                                                  width: 18,
                                                  height: 18,
                                                  background: "#fff",
                                                  pointerEvents: "none",
                                                  zIndex: 0,
                                                }}
                                              />
                                            </>
                                          ) : null}
                                          <div
                                            className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                                            style={{
                                              position: "relative",
                                              zIndex: 1,
                                              margin: 0,
                                              padding: "16px",
                                              background: pepReviewHeaderGlass
                                                ? "rgba(249, 247, 245, 0.72)"
                                                : "#F9F7F5",
                                              backdropFilter: pepReviewHeaderGlass
                                                ? "blur(16px) saturate(1.14)"
                                                : "none",
                                              WebkitBackdropFilter:
                                                pepReviewHeaderGlass
                                                  ? "blur(16px) saturate(1.14)"
                                                  : "none",
                                              borderTop:
                                                pepReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderLeft:
                                                pepReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderRight:
                                                pepReviewHeaderGlass
                                                  ? "1px solid rgba(222, 226, 230, 0.66)"
                                                  : "1px solid #DEE2E6",
                                              borderTopLeftRadius: 8,
                                              borderTopRightRadius: 8,
                                              boxShadow: pepReviewHeaderGlass
                                                ? "0 10px 26px rgba(45, 50, 60, 0.08)"
                                                : "none",
                                              transition:
                                                "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
                                            }}
                                          >
                                          <div
                                            className="d-flex gap-2"
                                            style={{
                                              alignItems: "center",
                                              minHeight: 58,
                                            }}
                                          >
                                            <span
                                              aria-hidden="true"
                                              className="d-inline-flex align-items-center justify-content-center"
                                              style={{
                                                width: 52,
                                                height: 58,
                                                flex: "0 0 52px",
                                                overflow: "visible",
                                              }}
                                            >
                                              <img
                                                src={contextIcon}
                                                alt=""
                                                aria-hidden="true"
                                                style={{
                                                  width: 50,
                                                  height: 50,
                                                  display: "block",
                                                  opacity: 1,
                                                  transform: "translateY(2px)",
                                                  transformOrigin: "center",
                                                }}
                                              />
                                            </span>
                                            <div
                                              className="d-flex flex-column justify-content-center"
                                              style={{ minHeight: 58 }}
                                            >
                                              <div
                                                className="fw-semibold"
                                                style={{ color: "#2f3441" }}
                                              >
                                                {toTitleCaseWords(group.name) ||
                                                  "Subject"}
                                              </div>

                                              {group.roles?.length ||
                                              !personDecision ? (
                                                <div className="d-flex gap-2 flex-wrap mt-2">
                                                  {group.roles.map((role) => (
                                                    <React.Fragment key={role}>
                                                      {roleBadge(role)}
                                                    </React.Fragment>
                                                  ))}
                                                  {!personDecision ? (
                                                    <span
                                                      className="squareBadge"
                                                      style={{
                                                        background: "#FEF9EE",
                                                        border:
                                                          "1px solid #DCD0A0",
                                                        color: "#7E6F3C",
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      Awaiting review
                                                    </span>
                                                  ) : null}
                                                </div>
                                              ) : null}
                                            </div>
                                          </div>

                                          <div className="d-flex flex-column align-items-end justify-content-center align-self-stretch" style={{ gap: 4 }}>
                                            {/* Person-level FINAL OUTCOME of this person's review.
                                                Question state: caption + amber cue + dashed empty pair.
                                                Verdict state: one stamped chip + a deliberate "Change". */}
                                            <div className="d-flex align-items-center gap-2">
                                            <div
                                              className={`d-flex align-items-center gap-3${!showDecisionChip ? " kycPepReviewOutcomePending" : ""}`}
                                              style={{
                                                background: showDecisionChip ? "#FCFAF8" : "#FEF9EE",
                                                border: "1px solid #E7E2DD",
                                                borderRadius: 10,
                                                padding: "8px 12px",
                                              }}
                                            >
                                              <div
                                                className="fw-semibold text-muted"
                                                style={{
                                                  fontSize: 11.5,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                Review outcome
                                              </div>
                                              <div
                                                aria-hidden="true"
                                                style={{
                                                  width: 1,
                                                  alignSelf: "stretch",
                                                  background: "#E7E2DD",
                                                }}
                                              />
                                              <div className="d-flex flex-column align-items-end gap-1">
                                              <div
                                                id={!showDecisionChip && hasSelectedEvidence ? `pep-evidence-guidance-${sectionNo}-${group.key}` : undefined}
                                                className="text-muted"
                                                style={{ fontSize: 11.5 }}
                                              >
                                                {showDecisionChip
                                                  ? isSubjectReviewed(group.key)
                                                    ? "Evidence below has been reviewed"
                                                    : "Add supporting information to complete review"
                                                  : hasSelectedEvidence
                                                  ? "Confirm PEP exposure or unselect hits"
                                                  : "Review the evidence below, then select"}
                                              </div>

                                              {showDecisionChip ? (
                                                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                                  <span
                                                    className="badge badgeStandard"
                                                    role={readOnly ? undefined : "button"}
                                                    title={
                                                      readOnly
                                                        ? undefined
                                                        : "Click to change"
                                                    }
                                                    onClick={
                                                      readOnly
                                                        ? undefined
                                                        : () =>
                                                            openDecisionEdit(
                                                              group.key
                                                            )
                                                    }
                                                    style={{
                                                      background:
                                                        personDecision ===
                                                        "exposure"
                                                          ? "#D69DAC"
                                                          : "#9bb0a5",
                                                      color: "#fff",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: 6,
                                                      height: 26,
                                                      padding: "0 10px",
                                                      cursor: readOnly
                                                        ? "default"
                                                        : "pointer",
                                                    }}
                                                  >
                                                    {personDecision === "exposure"
                                                      ? "PEP exposure"
                                                      : "No PEP exposure"}
                                                    {!readOnly ? (
                                                      <i
                                                        className="bi bi-pencil"
                                                        aria-hidden="true"
                                                        style={{ fontSize: 10, opacity: 0.85 }}
                                                      />
                                                    ) : null}
                                                  </span>
                                                </div>
                                              ) : (
                                                <div className="d-flex align-items-center flex-nowrap justify-content-end" style={{ gap: 4 }}>
                                                  <div
                                                    className="btn-group"
                                                    role="group"
                                                    aria-label="Person-level PEP final outcome"
                                                  >
                                                    <button
                                                      type="button"
                                                      className={`btn btn-sm ${
                                                        personDecision ===
                                                        "exposure" && !decisionEditing
                                                          ? "btn-toggle-on-lightgrey"
                                                          : "btn-toggle-off-lightgrey"
                                                      }`}
                                                      style={{
                                                        height: 26,
                                                        padding: "0 10px",
                                                        fontSize: 12,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        ...(personDecision ===
                                                        "exposure"
                                                          ? {
                                                              background: decisionEditing ? "#F8EFF2" : "#D69DAC",
                                                              borderColor: decisionEditing ? "#D9D9D9" : "#D69DAC",
                                                              color: decisionEditing ? "#6C757D" : "#fff",
                                                            }
                                                          : !personDecision
                                                          ? {
                                                              borderStyle:
                                                                "dashed",
                                                              borderColor:
                                                                "#B9BFC7",
                                                              background:
                                                                "#FFFFFF",
                                                            }
                                                          : {}),
                                                      }}
                                                      disabled={readOnly}
                                                      aria-pressed={personDecision === "exposure"}
                                                      onClick={() =>
                                                        setPersonDecision(
                                                          group.key,
                                                          "exposure"
                                                        )
                                                      }
                                                    >
                                                      PEP exposure
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className={`btn btn-sm kycNoExposureOption ${
                                                        personDecision ===
                                                        "no_exposure" && !decisionEditing
                                                          ? "btn-toggle-on-lightgrey"
                                                          : "btn-toggle-off-lightgrey"
                                                      }`}
                                                      style={{
                                                        height: 26,
                                                        padding: "0 10px",
                                                        fontSize: 12,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        ...(personDecision ===
                                                        "no_exposure"
                                                          ? {
                                                              background: decisionEditing ? "#F0F5F2" : "#9bb0a5",
                                                              borderColor: decisionEditing ? "#D9D9D9" : "#9bb0a5",
                                                              color: decisionEditing ? "#6C757D" : "#fff",
                                                            }
                                                          : !personDecision
                                                          ? {
                                                              borderStyle:
                                                                "dashed",
                                                              borderColor:
                                                                "#B9BFC7",
                                                              background:
                                                                "#FFFFFF",
                                                            }
                                                          : {}),
                                                      }}
                                                      disabled={readOnly || hasSelectedEvidence}
                                                      aria-describedby={!showDecisionChip && hasSelectedEvidence ? `pep-evidence-guidance-${sectionNo}-${group.key}` : undefined}
                                                      aria-pressed={personDecision === "no_exposure"}
                                                      onClick={() =>
                                                        setPersonDecision(
                                                          group.key,
                                                          "no_exposure"
                                                        )
                                                      }
                                                    >
                                                      No PEP exposure
                                                    </button>
                                                  </div>
                                                  {decisionEditing && personDecision && !readOnly ? (
                                                    <button
                                                      type="button"
                                                      className="btn btn-sm text-muted p-0 d-inline-flex align-items-center justify-content-center"
                                                      aria-label={`Clear review outcome for ${group.name}`}
                                                      style={{ width: 18, height: 18, flexShrink: 0, border: "1px solid #D9D9D9", borderRadius: "50%", background: "transparent" }}
                                                      onClick={() => setPersonDecision(group.key, null)}
                                                    >
                                                      <i className="bi bi-x-lg" aria-hidden="true" style={{ fontSize: 8, lineHeight: 1 }} />
                                                    </button>
                                                  ) : null}
                                                </div>
                                              )}
                                              </div>
                                            </div>
                                            </div>
                                          </div>
                                        </div>
                                        </div>

                                        <div
                                          style={{
                                            marginTop: 0,
                                            padding:
                                              evidenceCollapsed
                                                ? hasSelectedEvidence || needsSupportingReason || clean(pep?.supportingInformation?.[group.key])
                                                  ? "0 16px 20px"
                                                  : "0 16px 1px"
                                                : "0 16px 16px",
                                            borderLeft: "1px solid #DEE2E6",
                                            borderRight: "1px solid #DEE2E6",
                                            borderBottom: "1px solid #DEE2E6",
                                            borderBottomLeftRadius: 8,
                                            borderBottomRightRadius: 8,
                                            background: "#F9F7F5",
                                          }}
                                        >
                                        <AnimatePresence initial={false}>
                                        {webResearchIncomplete && !pepWebAssessmentLoading ? (
                                          <motion.div key="incomplete-research" {...evidenceCollapse} role="status" className="small text-muted py-2">
                                            {INCOMPLETE_PEP_RESEARCH_NOTICE}
                                          </motion.div>
                                        ) : null}
                                        {SHOW_AI_ASSESSMENT_CARD && !evidenceCollapsed ? (
                                        <motion.div
                                          key="ai-assessment"
                                          {...evidenceCollapse}
                                          style={{ overflow: "hidden" }}
                                        >
                                        <div
                                          className="rounded-3 border bg-white p-3"
                                          style={{ marginBottom: 12 }}
                                        >
                                          <div
                                            className="fw-semibold"
                                            style={{
                                              color: "#4C5465",
                                              fontSize: 13,
                                              marginBottom: 6,
                                            }}
                                          >
                                            AI assessment
                                          </div>

                                          {/* Combined-assessment content/logic still pending (Stage 2);
                                              for now this card carries the AI signal badges. */}
                                          <div className="d-flex gap-2 flex-wrap pepWebAssessmentBadges">
                                            {pepSignalBadge(signalLabel)}
                                            {aiItem
                                              ? pepWebContextMatchBadge(
                                                  aiItem?.result?.outcome
                                                )
                                              : pepWebAssessmentLoading
                                              ? badge("Assessing", "grey")
                                              : pepWebAssessmentErr
                                              ? badge(
                                                  "Assessment unavailable",
                                                  "grey"
                                                )
                                              : group.matches.length
                                              ? badge("Review needed", "grey")
                                              : null}
                                          </div>
                                        </div>
                                        </motion.div>
                                        ) : null}
                                        </AnimatePresence>

                                        {contextReviewChanged(group.key) ? <div className="identityContextMissing" role="status">KYC context has changed. Review the updated comparisons and confirm your outcome.</div> : null}
                                        <MissingContextNote meta={group.meta} roles={group.roles} readOnly={readOnly} />
                                        <div>
                                          <AnimatePresence initial={false}>
                                          {showPublicWebEvidence ? (
                                            <motion.div
                                              key="public-web-evidence"
                                              {...evidenceCollapse}
                                              style={{ overflow: "hidden" }}
                                            >
                                              <div
                                                className="text-muted"
                                                style={{
                                                  fontFamily: '"Nunito", sans-serif',
                                                  fontWeight: 500,
                                                  fontSize: 13,
                                                  marginBottom: 6,
                                                  borderTop: "1px solid rgba(121, 141, 160, 0.16)",
                                                  paddingTop: 10,
                                                }}
                                              >
                                                Potential matches · Public web findings
                                              </div>
                                              <div className="list-group" style={{ gap: 20 }}>
                                                {visibleWebCandidates.length ? visibleWebCandidates.map(candidate => (
                                                  <React.Fragment key={candidate.evidenceKey}>{renderAiResearchRow(group, candidate)}</React.Fragment>
                                                )) : renderAiResearchRow(group)}
                                              </div>
                                            </motion.div>
                                          ) : null}

                                          {visiblePersonMatches.length > 0 ? (
                                            <motion.div
                                              key="watchlist-evidence"
                                              {...evidenceCollapse}
                                              style={{ overflow: "hidden" }}
                                            >
                                              <div
                                                className="text-muted"
                                                style={{
                                                  fontFamily: '"Nunito", sans-serif',
                                                  fontWeight: 500,
                                                  fontSize: 13,
                                                  marginTop: 12,
                                                  marginBottom: 6,
                                                }}
                                              >
                                                Potential matches · Watchlist databases
                                              </div>
                                              <div className="list-group" style={{ gap: 20 }}>
                                                  <AnimatePresence initial={false}>
                                                    {visiblePersonMatches.map(
                                                      (match) => (
                                                        <motion.div
                                                          key={match._key}
                                                          {...evidenceCollapse}
                                                          style={{
                                                            overflow: "hidden",
                                                            borderRadius: "var(--bs-list-group-border-radius, 0.375rem)",
                                                          }}
                                                        >
                                                          {renderPepMatchRow(match)}
                                                        </motion.div>
                                                      )
                                                    )}
                                                  </AnimatePresence>
                                                </div>
                                            </motion.div>
                                          ) : null}
                                          {noPepFindings ? <div key="no-pep-findings" className="small text-muted" style={{padding: '10px 0', fontWeight: 500}}>
                                            <i className="bi bi-arrow-right me-2" style={{color: '#8A8F96'}} aria-hidden="true" />
                                            No potential PEP matches found
                                          </div> : null}
                                          {!evidenceCollapsed || needsSupportingReason || clean(pep?.supportingInformation?.[group.key]) ? (
                                          <motion.div
                                            key="supporting-information"
                                            {...evidenceCollapse}
                                            style={{ overflow: "hidden" }}
                                          >
                                          <label
                                            htmlFor={`pep-supporting-${sectionNo}-${group.key}`}
                                            className="d-block text-muted"
                                            style={{ fontFamily: '"Nunito", sans-serif', fontWeight: 500, fontSize: 13, marginTop: 12, marginBottom: 6 }}
                                          >
                                            Other supporting information
                                          </label>
                                          <div className="rounded-3 border bg-white p-3">
                                            <textarea
                                              id={`pep-supporting-${sectionNo}-${group.key}`}
                                              aria-label={`Other supporting information for ${group.name}`}
                                              className="form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line"
                                              rows={1}
                                              required={needsSupportingReason}
                                              placeholder={showDecisionChip && needsSupportingReason ? "No hit were flagged. Explain why this person has PEP exposure" : undefined}
                                              ref={(element) => autoResizeTextarea(element)}
                                              readOnly={readOnly}
                                              value={pep?.supportingInformation?.[group.key] || ""}
                                              onChange={(event) => {
                                                if (readOnly) return;
                                                setSupportingInformation(group.key, event.target.value);
                                                autoResizeTextarea(event.currentTarget);
                                              }}
                                            />
                                          </div>
                                          </motion.div>
                                          ) : null}
                                          </AnimatePresence>
                                        </div>
                                        </div>
                                      </motion.div>
                                    );
                                  })}
                                </AnimatePresence>
                              </motion.div>
                              <GoogleSearchAttribution items={pepWebAssessment} />
                            </div>

                          </>
                        ) : (
                          <div className="small mt-3">
                            No screening evidence to review yet.
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
        {/* --- END --- */}
      </div>
      {/* -- end main card -- */}
    </div>
  );
});

export default KycSectionPepWithScreening;
